export interface WarmupMessage {
  text: string;
  dots?: boolean; // animated dots — only for loading state messages
}

// ─── Loading states ──────────────────────────────────────────────────────────
const LOADING: WarmupMessage[] = [
  { text: "Preparing your workspace", dots: true },
  { text: "Connecting to your team", dots: true },
  { text: "Loading your configuration", dots: true },
  { text: "Setting up your dashboard", dots: true },
  { text: "Loading admin workspace", dots: true },
  { text: "Syncing your settings", dots: true },
  { text: "Almost there", dots: true },
  { text: "Loading the latest features", dots: true },
  { text: "Getting the latest updates", dots: true },
  { text: "Loading your command center", dots: true },
  { text: "Finalizing setup", dots: true },
  { text: "Initializing your experience", dots: true },
  { text: "Checking your permissions", dots: true }
];

// ─── Platform tips ───────────────────────────────────────────────────────────
const PLATFORM_TIPS: WarmupMessage[] = [
  { text: "Tip: Workflows can automate your most repetitive tasks" },
  { text: "Tip: Set up alerts before queue times get too long" },
  { text: "Tip: Use custom fields to capture key customer data" },
  { text: "Tip: Surveys help measure customer satisfaction after every chat" },
  { text: "Tip: Transfer conversations to the right agent in seconds" },
  { text: "Tip: Canned responses let agents reply in one click" },
  { text: "Tip: Check reports to track your team's performance" },
  { text: "Tip: Routing rules assign chats by skill or availability" },
  { text: "Tip: SMS and voice channels are just a click away" },
  { text: "Tip: Proactive chat can increase conversions by 30%" },
  { text: "Tip: Monitor live queues in the Operations Center" },
  { text: "Tip: Enable bot automation to handle off-hours traffic" },
  { text: "Tip: Use shadow mode to coach agents in real time" },
  { text: "Tip: CRM integrations sync customer data automatically" },
  { text: "Tip: Schedule adherence reports track agent punctuality" },
  { text: "Tip: Set SLA targets to keep wait times in check" },
  { text: "Tip: Use the contact timeline to see the full history" },
  { text: "Tip: Dark mode is available in your profile settings" },
  { text: "Tip: Configure team schedules for accurate availability" },
  { text: "Tip: Group agents by skill for smarter routing" },
  { text: "Tip: Agent-to-agent threads let teams collaborate on hard cases" },
  { text: "Tip: Auto-resolve keeps your inbox clean" },
  { text: "Tip: Queue alerts fire before wait times get critical" },
  { text: "Tip: Custom reports let you track exactly what matters" },
  { text: "Tip: Test bot flows before going live" },
  { text: "Tip: Check your service level reports weekly" },
  { text: "Tip: Set up office hours to manage availability" },
  { text: "Tip: Track resolution time to find bottlenecks" },
  { text: "Tip: Monitor bot escalation rates to improve your flows" },
  { text: "Tip: Webhook events connect your workspace to other tools" },
  {
    text: "Tip: Set up multiple teams to handle different products or regions"
  },
  {
    text: "Tip: Use survey triggers after resolution for automatic CSAT collection"
  },
  { text: "Tip: Check missed chat reports to recover lost opportunities" },
  { text: "Tip: Agent availability reports help you staff smarter" },
  {
    text: "Tip: Use the contact search to look up any customer's full history"
  },
  { text: "Tip: Invite teammates — the more agents, the shorter the queue" },
  { text: "Tip: Use conversation labels for easier filtering" },
  { text: "Tip: Use routing rules to send VIP customers to senior agents" },
  { text: "Tip: CSAT scores update in real time on the Operations Center" },
  { text: "Tip: Create multiple bot flows for different entry points" },
  { text: "Tip: Role-based access keeps admin tools in the right hands" },
  { text: "Tip: Use the API to build custom integrations" },
  {
    text: "Tip: Add knowledge base articles so your AI bot can answer customer questions faster"
  },
  { text: "Tip: Audit logs track every configuration change your team makes" },
  { text: "Tip: AI bots can qualify leads before handing off to an agent" },
  { text: "Tip: Export reports to CSV for deeper analysis in your BI tools" },
  {
    text: "Tip: Channel-aware workflows let bots behave differently on each channel"
  },
  { text: "Tip: Set up proactive triggers based on time spent on a page" },
  {
    text: "Tip: Knowledge base articles feed directly into your AI bot's answers"
  },
  {
    text: "Tip: Use conversation variables to personalize every bot interaction"
  },
  {
    text: "Tip: BigCommerce and Shopify integrations unlock commerce-aware bots"
  },
  {
    text: "Tip: Multi-step workflows can handle complex scenarios without an agent"
  },
  {
    text: "Tip: Tag conversations by topic to spot your most common support themes"
  },
  {
    text: "Tip: Pin your most-used pages in the sidebar for faster navigation"
  },
  {
    text: "Tip: Use variable passing to carry customer context across workflow steps"
  },
  {
    text: "Tip: A/B test your proactive invite messages to see which converts better"
  },
  {
    text: "Tip: Use the inbox view to spot conversations that have gone quiet"
  },
  {
    text: "Tip: Escalation tiers let bots hand off to the right specialist automatically"
  },
  {
    text: "Tip: Video chat is available on supported channels with one toggle"
  },
  { text: "Tip: Sentiment scoring updates live as a conversation progresses" },
  { text: "Tip: Use IVR voice bots to handle phone support around the clock" },
  {
    text: "Tip: AI orchestration lets multiple bots collaborate on complex queries"
  },
  {
    text: "Tip: Version history on workflows lets you roll back to any previous save"
  },
  {
    text: "Tip: Click-to-call widgets let visitors start a phone call in one tap"
  },
  {
    text: "Tip: E-commerce integrations give your bots access to order and product data"
  },
  {
    text: "Tip: Knowledge base articles improve AI accuracy on your specific topics"
  },
  {
    text: "Tip: Global prompts let you set brand voice rules across every bot"
  },
  {
    text: "Tip: Channel overrides let the same bot behave differently on SMS vs web"
  },
  {
    text: "Tip: Page widgets can target specific URLs to show relevant offers"
  },
  {
    text: "Tip: Agent whisper mode lets supervisors guide agents without the customer seeing"
  },
  {
    text: "Tip: Use conversation summaries to hand off context between shifts without re-reading the whole thread"
  },
  {
    text: "Tip: Set a max chat limit per agent so no one gets buried and quality stays high"
  },
  {
    text: "Tip: Trigger a workflow from a keyword — route chats before an agent even touches them"
  },
  {
    text: "Tip: Custom variables let you store and reuse any customer data across the conversation"
  },
  {
    text: "Tip: Use fallback messages in your bot flows so customers never hit a dead end"
  },
  {
    text: "Tip: Connect your CRM so agents see account history the moment a chat starts"
  },
  {
    text: "Tip: Use the audit log to trace any configuration change to the person who made it"
  },
  {
    text: "Tip: Offline forms capture leads even when no agents are available"
  },
  {
    text: "Tip: Tag conversations at close to build a searchable library of resolved issues"
  },
  {
    text: "Tip: Use conditional routing to send premium customers directly to your best agents"
  },
  {
    text: "Tip: The AI knowledge base updates within 24 hours of any article change"
  },
  {
    text: "Tip: Test your IVR flow in staging before pushing it live to customers"
  },
  {
    text: "Tip: WhatsApp business profiles increase customer trust before the conversation even starts"
  },
  {
    text: "Tip: Monitor average handle time to find coaching opportunities before they become trends"
  },
  {
    text: "Tip: Set up a dedicated team for after-hours so routing never fails at night"
  },
  {
    text: "Tip: Use bot analytics to find the exact step where customers drop off most often"
  },
  {
    text: "Tip: The global prompt sets your AI's tone and boundaries for every channel at once"
  },
  {
    text: "Tip: Escalation rules let bots automatically request a human after N failed attempts"
  },
  {
    text: "Tip: Use the contact timeline to understand the full journey before joining a conversation"
  },
  {
    text: "Tip: Skill-based routing ensures the right expertise reaches every customer every time"
  },
  {
    text: "Tip: Run your bot in shadow mode first — let it suggest responses while agents approve them"
  },
  {
    text: "Tip: Page-level targeting shows proactive invites only where they convert — not everywhere"
  },
  {
    text: "Tip: IVR menus can collect intent before the bot or agent even enters the conversation"
  },
  {
    text: "Tip: Use conversation exports to train your team on real examples of great resolution"
  },
  {
    text: "Tip: Satisfaction surveys sent within 5 minutes of close get 2× higher response rates"
  },
  {
    text: "Tip: Bot handoff messages that explain the transfer improve first-impression scores"
  },
  {
    text: "Tip: Use the Operations Center during peak hours to catch queue spikes before they escalate"
  },
  {
    text: "Tip: Assign a backup team to every primary team so routing never dead-ends"
  },
  {
    text: "Tip: Knowledge base categories help the AI route questions to the most relevant articles"
  },
  {
    text: "Tip: Use conversation labels to track which agent workflows need updating most"
  },
  {
    text: "Tip: Set auto-resolve rules for conversations that go quiet after resolution"
  },
  {
    text: "Tip: Use channel-specific greetings to match tone — formal for email, casual for web chat"
  },
  {
    text: "Tip: Track first-response time by team to spot workload imbalances early"
  },
  {
    text: "Tip: Use canned responses to answer FAQs in seconds without sacrificing a personal touch."
  },
  {
    text: "Tip: Set up skill-based routing so chats reach the agent best equipped to help."
  },
  {
    text: "Tip: Connect your CRM to Velaro so customer history is visible before you even say hello."
  },
  {
    text: "Tip: Schedule reports to land in your inbox weekly so trends never sneak past you."
  },
  {
    text: "Tip: Add pre-chat surveys to capture intent and route visitors to the right team instantly."
  },
  {
    text: "Tip: Use whisper mode to coach agents in real time without the customer seeing."
  },
  {
    text: "Tip: Build a knowledge base so your bot can deflect common questions around the clock."
  },
  {
    text: "Tip: Tag conversations consistently to unlock accurate reporting down the road."
  },
  {
    text: "Tip: Set business-hours rules so visitors see the right message when your team is offline."
  },
  {
    text: "Tip: Use post-chat surveys to measure satisfaction while the experience is still fresh."
  },
  {
    text: "Tip: Create workflow triggers that auto-assign VIP customers to your senior agents."
  },
  {
    text: "Tip: Monitor the real-time dashboard to spot queue spikes before they become wait-time issues."
  },
  {
    text: "Tip: Use transfer notes so the next agent picks up the conversation without asking twice."
  },
  {
    text: "Tip: Test your chatbot flow monthly — small tweaks can dramatically improve resolution rates."
  },
  {
    text: "Tip: Enable typing indicators so customers know you're actively working on their request."
  },
  {
    text: "Tip: Group agents into departments to keep routing clean as your team scales."
  },
  {
    text: "Tip: Export chat transcripts to your CRM automatically for a complete customer timeline."
  },
  {
    text: "Tip: Use canned responses to answer common questions in seconds, not minutes."
  },
  {
    text: "Tip: Set up skill-based routing so chats reach the agent best equipped to help."
  },
  {
    text: "Tip: Connect your CRM to Velaro so customer context is visible before you say hello."
  },
  {
    text: "Tip: Build a knowledge base so your bot can deflect repetitive questions automatically."
  },
  {
    text: "Tip: Use post-chat surveys to spot trends in customer satisfaction over time."
  },
  {
    text: "Tip: Tag conversations consistently — clean data makes your reports far more useful."
  },
  {
    text: "Tip: Set business-hour schedules so visitors see accurate availability on your widget."
  },
  {
    text: "Tip: Use whisper mode to coach new agents in real time without the customer seeing."
  },
  {
    text: "Tip: Review your chat transcripts weekly to find new topics for your knowledge base."
  },
  {
    text: "Tip: Automate follow-up emails after chat to keep the conversation going seamlessly."
  },
  {
    text: "Tip: Create workflow triggers that escalate chats when wait time exceeds your SLA."
  },
  {
    text: "Tip: Use visitor browsing data to anticipate questions before the customer even asks."
  },
  {
    text: "Tip: Group agents into departments so routing stays organized as your team scales."
  },
  {
    text: "Tip: Schedule reports to be emailed to stakeholders so metrics stay visible."
  },
  {
    text: "Tip: A/B test your proactive chat invitations to find what drives more engagement."
  },
  {
    text: "Tip: Set concurrent chat limits per agent to protect quality during busy periods."
  },
  {
    text: "Tip: Use custom fields in your chat forms to capture the data your team needs most."
  },
  {
    text: "Tip: Use chat routing rules to automatically assign conversations to your most qualified agents based on skills and availability."
  },
  {
    text: "Tip: Enable your knowledge base in the chat widget so customers can self-serve answers before talking to an agent."
  },
  {
    text: "Tip: Set up canned responses for common questions to reply faster and maintain consistency across your team."
  },
  {
    text: "Tip: Configure bot handoff rules to smoothly transition conversations from automation to human agents when needed."
  },
  {
    text: "Tip: Use tags to organize conversations by topic, product, or issue type for better reporting and team insights."
  },
  {
    text: "Tip: Enable proactive chat invitations to engage visitors on high-intent pages and reduce support ticket volume."
  },
  {
    text: "Tip: Sync your CRM with Velaro to access customer history and context without leaving the chat interface."
  },
  {
    text: "Tip: Create separate chat queues for different departments to streamline routing and improve response times."
  },
  {
    text: "Tip: Monitor real-time dashboards to track agent availability, wait times, and chat volume throughout the day."
  },
  {
    text: "Tip: Build custom workflows to automate follow-ups, surveys, and ticket creation after chat conversations end."
  },
  {
    text: "Tip: Use sentiment analysis in reports to identify conversations that need manager review or agent coaching."
  },
  {
    text: "Tip: Configure offline messages to capture leads and questions when no agents are available online."
  },
  {
    text: "Tip: Set up chat transcripts to be automatically saved and searchable for compliance and quality assurance."
  },
  {
    text: "Tip: Create bot conversations for password resets, appointment scheduling, or account status checks to save agent time."
  },
  {
    text: "Tip: Use visitor segmentation to show different chat widgets based on geography, behavior, or customer type."
  },
  {
    text: "Tip: Enable agent notes to document internal observations during chats without customers seeing them."
  },
  {
    text: "Tip: Build escalation workflows to automatically route complex issues to senior agents or supervisors."
  },
  {
    text: "Tip: Configure chat availability windows to set when your team is online and ready to support customers."
  },
  {
    text: "Tip: Use performance reports to identify your top agents and learn what makes their conversations successful."
  },
  {
    text: "Tip: Create targeted knowledge base articles from frequently asked chat questions to improve self-service rates."
  },
  {
    text: "Tip: Set up chat rating surveys to gather feedback and identify areas for agent training and improvement."
  },
  {
    text: "Tip: Use conversation search to find past chats by customer, topic, or keywords for faster issue resolution."
  },
  {
    text: "Tip: Configure department-specific greetings to personalize the chat experience based on visitor needs."
  },
  {
    text: "Tip: Enable mobile push notifications so agents can respond to chats even when away from their desks."
  },
  {
    text: "Tip: Build automated pre-chat forms to qualify leads and route them to the right department immediately."
  },
  {
    text: "Tip: Use chat history integration to show customers previous conversations and maintain context across sessions."
  },
  {
    text: "Tip: Set up speed-to-answer metrics in reporting to track how quickly your team engages new visitors."
  },
  {
    text: "Tip: Create chatbot flows that ask qualifying questions before escalating to human agents for faster resolution."
  },
  {
    text: "Tip: Configure agent availability statuses to prevent chats from routing to agents who are on break or busy."
  },
  {
    text: "Tip: Use conversation recording features to train new agents and review best practices from experienced team members."
  },
  {
    text: "Tip: Enable geolocation targeting to customize chat messages based on where your website visitors are located."
  },
  {
    text: "Tip: Set up automated responses for out-of-hours chat requests with expected response time information."
  },
  {
    text: "Tip: Use CRM field mapping to automatically populate customer data into Velaro from your existing systems."
  },
  {
    text: "Tip: Create agent groups with different skill sets to ensure specialized issues reach the most qualified person."
  },
  {
    text: "Tip: Configure chat sounds and desktop notifications so agents never miss an incoming conversation request."
  },
  {
    text: "Tip: Build knowledge base categories that mirror your support team's expertise areas for easy navigation."
  },
  {
    text: "Tip: Use visitor tracking to see what pages customers browse before starting a chat conversation."
  },
  {
    text: "Tip: Set up periodic reports to measure chat volume trends and plan staffing for peak support hours."
  },
  {
    text: "Tip: Enable custom chat fields to collect specific information relevant to your business during each conversation."
  },
  {
    text: "Tip: Create bot intents that understand customer language variations to route conversations more accurately."
  },
  {
    text: "Tip: Use agent performance dashboards to celebrate wins and identify where additional training might help."
  },
  {
    text: "Tip: Configure chat widget appearance to match your brand colors, fonts, and logos for a consistent experience."
  },
  {
    text: "Tip: Enable conversation import from email to unify all customer communications in one platform."
  },
  {
    text: "Tip: Set up automated handoff notifications so agents know why a chat was transferred to them."
  },
  {
    text: "Tip: Use workflow conditions to trigger different actions based on customer type, issue urgency, or behavior."
  },
  {
    text: "Tip: Configure language preferences in your knowledge base to support multilingual customer conversations."
  },
  {
    text: "Tip: Enable chat transcript export so customers receive email summaries of their conversation for reference."
  },
  {
    text: "Tip: Build dependency rules in workflows so certain automation steps only trigger when specific conditions are met."
  },
  {
    text: "Tip: Use agent collision detection to prevent multiple agents from responding to the same chat simultaneously."
  },
  {
    text: "Tip: Set up monthly knowledge base audits to remove outdated information and keep content accurate."
  },
  {
    text: "Tip: Use routing rules to automatically assign chats based on agent skills and availability for faster resolution."
  },
  {
    text: "Tip: Set up canned responses for frequently asked questions to reduce response time and ensure consistency."
  },
  {
    text: "Tip: Enable your knowledge base widget on your website so customers can self-serve before starting a chat."
  },
  {
    text: "Tip: Create separate chat queues for different departments to streamline workflow and improve organization."
  },
  {
    text: "Tip: Use tags to categorize conversations by topic, making historical data easier to search and analyze."
  },
  {
    text: "Tip: Set up automated greetings to welcome visitors and collect information before an agent joins the chat."
  },
  {
    text: "Tip: Configure agent availability status to prevent chats from routing to offline or busy team members."
  },
  {
    text: "Tip: Use the CRM integration to pull customer history directly into Velaro for more personalized support."
  },
  {
    text: "Tip: Create workflows to automatically escalate chats that exceed a certain wait time or complexity level."
  },
  {
    text: "Tip: Enable chat transcripts via email so customers have a record of their support conversation."
  },
  {
    text: "Tip: Use department routing to ensure billing questions reach the right team, not sales or support."
  },
  {
    text: "Tip: Set up bot responses for after-hours to collect contact info and create tickets for the next business day."
  },
  {
    text: "Tip: Monitor chat metrics like average response time to identify coaching opportunities for your team."
  },
  {
    text: "Tip: Use custom fields in your CRM integration to capture specific data relevant to your business needs."
  },
  {
    text: "Tip: Create a knowledge base article for every common question to reduce repetitive chat conversations."
  },
  {
    text: "Tip: Enable visitor identification so returning customers are recognized automatically during chats."
  },
  {
    text: "Tip: Set up proactive chat invitations to offer help to visitors browsing specific pages on your site."
  },
  {
    text: "Tip: Use the reporting dashboard to track agent performance and identify training needs."
  },
  {
    text: "Tip: Configure chat routing to distribute conversations evenly across your team to prevent burnout."
  },
  {
    text: "Tip: Create bot workflows that ask qualifying questions before routing chats to human agents."
  },
  {
    text: "Tip: Use conditional routing rules to send urgent issues to senior agents or specialized teams."
  },
  {
    text: "Tip: Enable mobile notifications so agents can respond to chats even when away from their desk."
  },
  {
    text: "Tip: Set up automated follow-up messages to check in on resolved issues after 24 hours."
  },
  {
    text: "Tip: Use the transcript search feature to find past solutions and improve consistency across your team."
  },
  {
    text: "Tip: Configure agent groups by timezone to ensure 24/7 coverage without overstaffing."
  },
  {
    text: "Tip: Create knowledge base categories that match your product or service structure for easy navigation."
  },
  {
    text: "Tip: Use conversation tags to track which topics drive the most support volume each month."
  },
  {
    text: "Tip: Set up chatbot handoff rules so bots know exactly when to transfer to a human agent."
  },
  {
    text: "Tip: Enable customer satisfaction surveys after chats to gather feedback and identify improvement areas."
  },
  {
    text: "Tip: Use the CRM integration to automatically log all customer interactions in one central location."
  },
  {
    text: "Tip: Create separate workflows for different customer segments to personalize the support experience."
  },
  {
    text: "Tip: Configure agent status shortcuts to make it quick to switch between available and on break."
  },
  {
    text: "Tip: Use chat history to upsell or cross-sell by reviewing what customers have previously purchased."
  },
  {
    text: "Tip: Set up bot escalation rules so complex issues are flagged for immediate agent review."
  },
  {
    text: "Tip: Enable co-browsing to see exactly what customers are viewing when they describe their issue."
  },
  {
    text: "Tip: Use routing rules to send VIP customers to your most experienced agents automatically."
  },
  {
    text: "Tip: Create a knowledge base search widget to answer questions faster during active chats."
  },
  {
    text: "Tip: Set up automated chat routing based on language preference for multilingual support teams."
  },
  {
    text: "Tip: Use agent notes to document special customer needs or preferences for future interactions."
  },
  {
    text: "Tip: Configure webhook integrations to sync chat data with your existing tools and systems."
  },
  {
    text: "Tip: Enable agent collision prevention to ensure two agents don't respond to the same chat."
  },
  {
    text: "Tip: Use the reporting API to build custom dashboards that track metrics important to your business."
  },
  {
    text: "Tip: Set up chat templates for different scenarios so agents follow a consistent support process."
  },
  {
    text: "Tip: Create bot responses that provide immediate answers while customers wait for human assistance."
  },
  {
    text: "Tip: Use custom routing logic to prioritize chats from high-value customers or specific account tiers."
  },
  {
    text: "Tip: Enable offline message capture so you never miss inquiries when your team is unavailable."
  },
  {
    text: "Tip: Set up automated ticket creation from chats so nothing gets lost in follow-up."
  },
  {
    text: "Tip: Use department-specific knowledge bases so agents only see articles relevant to their queue."
  },
  {
    text: "Tip: Configure chat timeouts to automatically close inactive conversations and free up agent capacity."
  },
  {
    text: "Tip: Use sentiment analysis in your CRM to identify frustrated customers who need special attention."
  },
  {
    text: "Tip: Use routing rules to automatically assign chats to the best agent based on skill or department."
  },
  {
    text: "Tip: Set up canned responses for common questions to reduce response time and improve consistency."
  },
  {
    text: "Tip: Enable proactive chat invitations on high-traffic pages to increase engagement and conversions."
  },
  {
    text: "Tip: Create bot workflows to handle FAQs and collect visitor information before agent handoff."
  },
  {
    text: "Tip: Use visitor attributes to personalize greetings and provide context to agents instantly."
  },
  {
    text: "Tip: Set up chat transcripts to be automatically saved to your CRM for better customer history."
  },
  {
    text: "Tip: Monitor chat duration metrics to identify bottlenecks and improve efficiency in your queue."
  },
  {
    text: "Tip: Use departments to organize teams and route conversations to the right specialists."
  },
  {
    text: "Tip: Enable offline messages so customers can reach you even when your support team is unavailable."
  },
  {
    text: "Tip: Create a knowledge base article library to reduce repetitive questions in your chat queue."
  },
  {
    text: "Tip: Use sentiment analysis in reporting to identify customer satisfaction trends over time."
  },
  {
    text: "Tip: Set up automated handoff rules to move complex chats from bots to agents seamlessly."
  },
  {
    text: "Tip: Create custom fields to capture specific information relevant to your business during chats."
  },
  {
    text: "Tip: Use chat tags to organize and categorize conversations for better reporting and analysis."
  },
  {
    text: "Tip: Enable co-browsing to help agents see what visitors are viewing and provide better support."
  },
  {
    text: "Tip: Set up performance dashboards to track agent response times and customer satisfaction scores."
  },
  {
    text: "Tip: Create bot intents to recognize customer intent and route to appropriate resources faster."
  },
  {
    text: "Tip: Use visitor segmentation to show different chat widgets to different audience groups."
  },
  {
    text: "Tip: Enable chat history search so agents can quickly review previous interactions with customers."
  },
  {
    text: "Tip: Set up escalation workflows to automatically bump complex issues to senior support staff."
  },
  {
    text: "Tip: Use real-time reporting to monitor chat queue depth and agent availability throughout the day."
  },
  {
    text: "Tip: Create knowledge base categories that match your most common customer questions and topics."
  },
  {
    text: "Tip: Enable form pre-fill to save customers time and collect accurate information automatically."
  },
  {
    text: "Tip: Use agent availability status to prevent routing chats when your team is at capacity."
  },
  {
    text: "Tip: Set up scheduled bot responses to handle peak traffic times with automated support."
  },
  {
    text: "Tip: Create custom reports to measure the ROI of your chat support on sales and retention."
  },
  {
    text: "Tip: Use chat transfer features to smoothly move conversations between agents without losing context."
  },
  {
    text: "Tip: Enable post-chat surveys to gather feedback and identify areas for improvement in real time."
  },
  {
    text: "Tip: Create workflow triggers based on customer behavior to automate proactive outreach campaigns."
  },
  {
    text: "Tip: Use agent groups to organize teams by timezone or specialization for better routing efficiency."
  },
  {
    text: "Tip: Enable chat replay to review conversations and coach agents on best practices and techniques."
  },
  {
    text: "Tip: Set up integration webhooks to send chat data directly into your business intelligence tools."
  },
  {
    text: "Tip: Create bot fallback responses for questions it cannot answer to ensure helpful guidance."
  },
  {
    text: "Tip: Use visitor browser session data to understand context before agents begin conversations."
  },
  {
    text: "Tip: Enable mobile-optimized chat widgets so customers can reach you on any device or platform."
  },
  {
    text: "Tip: Set up chat availability windows to show when support is available in specific time zones."
  },
  {
    text: "Tip: Create agent performance goals and track metrics like resolution rate and customer satisfaction."
  },
  {
    text: "Tip: Use chatbot conversation logs to identify gaps and improve your automation over time."
  },
  {
    text: "Tip: Enable email-to-chat features so customer emails can be converted to chat conversations."
  },
  {
    text: "Tip: Set up announcement banners to inform visitors about response times and service updates."
  },
  {
    text: "Tip: Create custom bot personalities that match your brand voice and company culture values."
  },
  {
    text: "Tip: Use API integrations to sync customer data from your existing systems into Velaro automatically."
  },
  {
    text: "Tip: Enable agent notes within chats to document important details without showing customers."
  },
  {
    text: "Tip: Set up department-specific knowledge bases so agents see only relevant help articles instantly."
  },
  {
    text: "Tip: Create chat widgets with custom colors and branding to match your website design seamlessly."
  },
  {
    text: "Tip: Use visitor geolocation data to route chats to agents in the same region when possible."
  },
  {
    text: "Tip: Enable chat ratings to collect immediate feedback and identify patterns in customer satisfaction."
  },
  {
    text: "Tip: Set up automated chat summaries to document key points and action items after each conversation."
  },
  {
    text: "Tip: Create bot-to-human handoff scripts that provide agents with context about the previous bot interaction."
  },
  {
    text: "Tip: Use advanced filtering in reports to isolate specific metrics by agent, department, or time period."
  },
  {
    text: "Tip: Use chat routing rules to automatically assign visitors based on department, language, or skill level."
  },
  {
    text: "Tip: Set up canned responses for common questions to reduce response time and maintain consistency."
  },
  {
    text: "Tip: Enable proactive chat invitations on high-traffic pages to increase engagement and conversions."
  },
  {
    text: "Tip: Use the CRM integration to pull customer history directly into your chat window."
  },
  {
    text: "Tip: Create bot workflows to qualify leads before routing them to human agents."
  },
  {
    text: "Tip: Monitor chat metrics like response time and satisfaction scores in your reporting dashboard."
  },
  {
    text: "Tip: Set up offline messages so visitors can leave feedback when no agents are available."
  },
  {
    text: "Tip: Use knowledge base articles in chat to provide instant answers without agent involvement."
  },
  {
    text: "Tip: Create agent groups based on expertise to ensure visitors reach the right specialist."
  },
  {
    text: "Tip: Enable visitor chat history tracking to give agents context on previous conversations."
  },
  {
    text: "Tip: Use performance reports to identify peak traffic times and staff accordingly."
  },
  {
    text: "Tip: Set up automated workflow triggers to escalate urgent chats to senior team members."
  },
  {
    text: "Tip: Connect Velaro to your CRM to automatically log all chat interactions."
  },
  {
    text: "Tip: Use chat transcripts in your knowledge base to identify common customer pain points."
  },
  {
    text: "Tip: Configure bot handoff rules to transition seamlessly to human agents when needed."
  },
  {
    text: "Tip: Track abandoned chats in your reports to understand where visitors are dropping off."
  },
  {
    text: "Tip: Use custom fields in chat forms to collect specific information upfront."
  },
  {
    text: "Tip: Set availability hours to manage customer expectations about response times."
  },
  {
    text: "Tip: Create targeted chat campaigns for specific customer segments or visitor behaviors."
  },
  {
    text: "Tip: Use the activity log to review agent interactions and provide coaching feedback."
  },
  {
    text: "Tip: Enable chat notifications so agents never miss an incoming visitor conversation."
  },
  {
    text: "Tip: Set up bot responses for after-hours to acknowledge chats and gather basic info."
  },
  {
    text: "Tip: Use the visitor list to see who's currently on your site and their browsing activity."
  },
  {
    text: "Tip: Create workflow rules to automatically send follow-up messages after chat ends."
  },
  {
    text: "Tip: Monitor agent utilization reports to optimize staffing levels."
  },
  {
    text: "Tip: Use chat tags to categorize conversations by topic for better analysis."
  },
  {
    text: "Tip: Enable chat ratings to gather feedback and identify improvement areas."
  },
  {
    text: "Tip: Create bot flows that collect information to pre-fill CRM records."
  },
  {
    text: "Tip: Use reporting to track chat conversion rates and ROI by marketing channel."
  },
  {
    text: "Tip: Set up intelligent routing to distribute chats evenly among available agents."
  },
  {
    text: "Tip: Use canned responses with variables to personalize quick replies."
  },
  {
    text: "Tip: Enable transcript emails so customers have a record of their conversation."
  },
  {
    text: "Tip: Create separate bot personas for different departments or customer types."
  },
  {
    text: "Tip: Use the dashboard to monitor real-time chat metrics and agent performance."
  },
  {
    text: "Tip: Set up knowledge base suggestions in chat to help agents answer faster."
  },
  {
    text: "Tip: Create workflows that trigger based on visitor page, time on site, or behavior."
  },
  {
    text: "Tip: Use chat reports to measure customer satisfaction trends over time."
  },
  {
    text: "Tip: Enable mobile-optimized chat to provide seamless support on all devices."
  },
  {
    text: "Tip: Create agent roles with specific permissions to maintain security and structure."
  },
  {
    text: "Tip: Use visitor segmentation to show different chat widgets to different audiences."
  },
  {
    text: "Tip: Set up automated responses for common objections in your bot workflow."
  },
  {
    text: "Tip: Monitor chat quality by reviewing transcripts and agent performance metrics."
  },
  {
    text: "Tip: Create templates for complex chat scenarios to ensure consistent communication."
  },
  {
    text: "Tip: Use the integration hub to connect Velaro with your other business tools."
  },
  {
    text: "Tip: Set up chat escalation rules to automatically flag complex issues for review."
  },
  {
    text: "Tip: Use reporting to identify your top-performing agents and best practices."
  },
  {
    text: "Tip: Create bot logic that learns from common chat outcomes to improve responses."
  },
  {
    text: "Tip: Enable chat availability indicators to show visitors if someone can help now."
  },
  {
    text: "Tip: Use the feedback system to continuously refine your chat strategy."
  },
  {
    text: "Tip: Create separate queues for different chat types to manage volume effectively."
  },
  {
    text: "Tip: Use routing rules to automatically assign chats to agents based on department or skill level."
  },
  {
    text: "Tip: Set up canned responses for common questions to reduce reply time by up to 80%."
  },
  {
    text: "Tip: Enable chat transcripts so customers can review conversations via email after sessions end."
  },
  {
    text: "Tip: Leverage your knowledge base widget to offer self-service answers before human intervention."
  },
  {
    text: "Tip: Create separate chat queues for different product lines to improve first-contact resolution."
  },
  {
    text: "Tip: Use the pre-chat form to collect customer data and route conversations more intelligently."
  },
  {
    text: "Tip: Set agent availability statuses to control workload distribution during peak hours."
  },
  {
    text: "Tip: Enable chatbot escalation rules to hand off complex issues to human agents seamlessly."
  },
  {
    text: "Tip: Use tags to categorize conversations by topic for better reporting and analysis later."
  },
  {
    text: "Tip: Integrate your CRM to display customer history and order details in the chat window."
  },
  {
    text: "Tip: Create workflow automations to send follow-up messages after chat sessions close."
  },
  {
    text: "Tip: Use the analytics dashboard to identify peak traffic times and plan staffing accordingly."
  },
  {
    text: "Tip: Set up proactive chat invitations to engage visitors before they request support."
  },
  {
    text: "Tip: Enable offline messages so visitors can submit requests when no agents are available."
  },
  {
    text: "Tip: Use sentiment analysis to flag high-priority or frustrated customers for special attention."
  },
  {
    text: "Tip: Create agent groups by language capability to serve international customers better."
  },
  {
    text: "Tip: Use custom fields to capture specific data points relevant to your business."
  },
  {
    text: "Tip: Enable chat transfer between agents to maintain context during handoffs."
  },
  {
    text: "Tip: Set up triggered workflows based on customer behavior like cart abandonment."
  },
  {
    text: "Tip: Use the conversation search feature to find past interactions quickly for compliance audits."
  },
  {
    text: "Tip: Create templates for common workflows to save time and ensure consistency across team."
  },
  {
    text: "Tip: Enable agent collaboration features so teammates can assist with complex questions."
  },
  {
    text: "Tip: Use real-time alerts to notify managers when customer satisfaction scores dip below target."
  },
  {
    text: "Tip: Set up knowledge base articles to auto-suggest during chats based on conversation content."
  },
  {
    text: "Tip: Create custom reports to track metrics that matter most to your business goals."
  },
  {
    text: "Tip: Use the visitor tracking feature to understand customer journey before they initiate chat."
  },
  {
    text: "Tip: Enable department routing to ensure customers reach the right specialist immediately."
  },
  {
    text: "Tip: Set up automated surveys post-chat to gather feedback and identify improvement areas."
  },
  {
    text: "Tip: Use API integrations to connect Velaro with your existing business tools seamlessly."
  },
  {
    text: "Tip: Create skill-based routing to match customers with agents who specialize in their needs."
  },
  {
    text: "Tip: Use the chat history search to quickly locate previous conversations by customer name."
  },
  {
    text: "Tip: Enable browser notifications so agents don't miss incoming chats while multitasking."
  },
  {
    text: "Tip: Set up abandoned chat workflows to re-engage customers who left without resolution."
  },
  {
    text: "Tip: Use custom branding in the chat widget to maintain consistent brand experience."
  },
  {
    text: "Tip: Create agent performance dashboards to identify top performers and training opportunities."
  },
  {
    text: "Tip: Enable mobile chat support so agents can respond from anywhere, anytime."
  },
  {
    text: "Tip: Use scheduled workflows to send proactive messages during specific times or events."
  },
  {
    text: "Tip: Set up integration with email systems to create tickets from chat conversations."
  },
  {
    text: "Tip: Use the conversation activity log to audit all actions taken within a single chat session."
  },
  {
    text: "Tip: Create dynamic routing rules that adjust based on current agent availability and skill."
  },
  {
    text: "Tip: Enable audio and video capabilities to enhance customer experience beyond text chat."
  },
  {
    text: "Tip: Use predictive analytics to forecast support volume and plan resources in advance."
  },
  {
    text: "Tip: Set up feedback loops by connecting CSAT surveys directly to agent coaching."
  },
  {
    text: "Tip: Create chat scripts for frontline agents to maintain quality across all interactions."
  },
  {
    text: "Tip: Use department-level reporting to understand performance across your support teams."
  },
  {
    text: "Tip: Enable chat masking to protect sensitive customer data in transcripts and reports."
  },
  {
    text: "Tip: Set up automated responses for after-hours chats to set expectations for reply time."
  },
  {
    text: "Tip: Use the visitor session replay feature to understand exactly what customers experienced."
  },
  {
    text: "Tip: Create escalation paths based on wait time so high-priority chats get immediate attention."
  },
  {
    text: "Tip: Enable integration with your ticketing system to maintain one unified view of support."
  },
  {
    text: "Tip: Use routing rules to automatically assign chats based on agent skills and availability."
  },
  {
    text: "Tip: Enable chat transcripts in your CRM to maintain a complete customer interaction history."
  },
  {
    text: "Tip: Set up chatbot triggers for common questions to reduce response times significantly."
  },
  {
    text: "Tip: Customize your knowledge base categories to match your customer's mental model."
  },
  {
    text: "Tip: Monitor agent utilization reports to identify understaffed periods and adjust scheduling."
  },
  {
    text: "Tip: Create workflow automations to send follow-up messages after chat sessions end."
  },
  {
    text: "Tip: Use tags in your CRM to segment customers by issue type for better reporting."
  },
  {
    text: "Tip: Enable proactive chat invitations on high-traffic pages to increase engagement rates."
  },
  {
    text: "Tip: Set response time SLAs for different chat queues to maintain service standards."
  },
  {
    text: "Tip: Integrate your ticketing system to seamlessly escalate complex issues from chat."
  },
  {
    text: "Tip: Create canned responses for frequently asked questions to speed up agent responses."
  },
  {
    text: "Tip: Use chat analytics to identify your most common customer pain points and issues."
  },
  {
    text: "Tip: Set up customer satisfaction surveys immediately after chat to gather feedback."
  },
  {
    text: "Tip: Configure bot handoff rules to smoothly transition conversations to human agents."
  },
  {
    text: "Tip: Use agent availability statuses to control chat routing during breaks and shifts."
  },
  {
    text: "Tip: Enable chat history viewing so returning customers don't repeat their problem."
  },
  {
    text: "Tip: Create separate chat queues for different departments to improve routing accuracy."
  },
  {
    text: "Tip: Use workflow conditions to automatically escalate urgent messages to supervisors."
  },
  {
    text: "Tip: Set up knowledge base articles as suggested resources during live chat sessions."
  },
  {
    text: "Tip: Monitor your chat abandonment rate to identify when customers are giving up."
  },
  {
    text: "Tip: Create custom fields in your CRM to track customer-specific information during chats."
  },
  {
    text: "Tip: Use bot confidence scoring to route uncertain responses to human agents automatically."
  },
  {
    text: "Tip: Enable typing indicators to show customers that agents are actively helping them."
  },
  {
    text: "Tip: Set up department-specific canned responses for consistent messaging across your team."
  },
  {
    text: "Tip: Use conversion tracking to measure how many chats result in sales or desired actions."
  },
  {
    text: "Tip: Configure auto-assignment rules to balance chat load evenly across your team."
  },
  {
    text: "Tip: Create bot fallback responses that gracefully hand off to human agents."
  },
  {
    text: "Tip: Use sentiment analysis in reports to identify negative customer interactions early."
  },
  {
    text: "Tip: Enable offline messages so customers can leave details when agents aren't available."
  },
  {
    text: "Tip: Set up chat routing based on customer tier or lifetime value for VIP support."
  },
  {
    text: "Tip: Use real-time dashboards to monitor active chats, wait times, and agent status."
  },
  {
    text: "Tip: Create knowledge base search suggestions that appear as customers type questions."
  },
  {
    text: "Tip: Set up automated greetings that personalize messages using customer data from CRM."
  },
  {
    text: "Tip: Monitor first response time metrics to identify bottlenecks in your chat workflow."
  },
  {
    text: "Tip: Use chat transcripts to train new agents on your best practices and responses."
  },
  {
    text: "Tip: Enable file sharing in chat so agents can send screenshots, documents, or resources."
  },
  {
    text: "Tip: Create separate bot paths for different customer segments or products offered."
  },
  {
    text: "Tip: Set up post-chat workflows to automatically create CRM records or support tickets."
  },
  {
    text: "Tip: Use reporting filters to compare agent performance across different time periods."
  },
  {
    text: "Tip: Enable chat ratings to let customers immediately provide quality feedback."
  },
  {
    text: "Tip: Create workflow rules that log specific customer information to your CRM automatically."
  },
  {
    text: "Tip: Use agent groups to organize teams by skill set and route chats accordingly."
  },
  {
    text: "Tip: Set up knowledge base categories that mirror your most common customer questions."
  },
  {
    text: "Tip: Enable chat notifications so agents never miss an incoming customer message."
  },
  {
    text: "Tip: Create bot intents that match your customer's actual language, not technical terms."
  },
  {
    text: "Tip: Use chat context windows to show agents previous interactions with returning customers."
  },
  {
    text: "Tip: Set up escalation workflows that notify supervisors when chats exceed time limits."
  },
  {
    text: "Tip: Enable customer identity verification within chat to protect sensitive information."
  },
  {
    text: "Tip: Create reporting dashboards that focus on metrics your leadership team actually cares about."
  },
  {
    text: "Tip: Use workflow branching to route customers to specialists based on their specific issue."
  },
  {
    text: "Tip: Use chat routing rules to automatically assign visitors to agents by language, skill, or department."
  },
  {
    text: "Tip: Enable your knowledge base widget to let customers find answers before starting a live chat."
  },
  {
    text: "Tip: Set up bot escalation workflows to smoothly hand off conversations to human agents when needed."
  },
  {
    text: "Tip: Create canned responses for common questions to speed up reply times without sacrificing quality."
  },
  {
    text: "Tip: Use visitor tags in your CRM to segment customers and personalize their support experience."
  },
  {
    text: "Tip: Monitor agent availability in real time to prevent visitor queues during peak hours."
  },
  {
    text: "Tip: Configure chat pre-chat forms to collect visitor information before agents join conversations."
  },
  {
    text: "Tip: Use conversation history to understand customer context and resolve issues faster."
  },
  {
    text: "Tip: Set up automated chat transcripts to maintain records for compliance and quality assurance."
  },
  {
    text: "Tip: Create workflow triggers based on visitor behavior to proactively offer help at the right moment."
  },
  {
    text: "Tip: Integrate your CRM to automatically load customer profiles when a chat starts."
  },
  {
    text: "Tip: Use the reporting dashboard to track response times and identify coaching opportunities."
  },
  {
    text: "Tip: Set agent status to 'Away' during breaks to prevent chat assignments when unavailable."
  },
  {
    text: "Tip: Use conversation notes to share context with teammates on handoffs or follow-ups."
  },
  {
    text: "Tip: Enable chat sounds and notifications to ensure agents never miss incoming messages."
  },
  {
    text: "Tip: Create bot scripts that answer FAQs instantly, reducing support volume by up to 40 percent."
  },
  {
    text: "Tip: Use geolocation targeting to route international chats to agents fluent in that region's language."
  },
  {
    text: "Tip: Track customer satisfaction metrics per agent to recognize top performers and guide improvement."
  },
  {
    text: "Tip: Set up offload rules to route chats to a knowledge base or form when all agents are busy."
  },
  {
    text: "Tip: Use custom fields in your CRM integration to track customer segments or subscription tiers."
  },
  {
    text: "Tip: Enable chat history search to quickly locate past conversations with the same customer."
  },
  {
    text: "Tip: Create separate bot workflows for sales inquiries, support tickets, and feedback collection."
  },
  {
    text: "Tip: Use agent groups to organize your team by skill, language, or product specialization."
  },
  {
    text: "Tip: Configure visitor invite rules to prompt inactive browsers to start a chat conversation."
  },
  {
    text: "Tip: Monitor abandonment rates in your reporting to see where customers are dropping off."
  },
  {
    text: "Tip: Use smart routing to match chat complexity with agent experience levels automatically."
  },
  {
    text: "Tip: Set up automated surveys after chat to gather customer feedback on agent performance."
  },
  {
    text: "Tip: Create knowledge base categories that mirror your bot conversation flows for consistency."
  },
  {
    text: "Tip: Use API webhooks to sync chat data with external business tools in real time."
  },
  {
    text: "Tip: Enable agent collision detection to prevent multiple agents from joining the same chat."
  },
  {
    text: "Tip: Set up chat transfer workflows to move conversations between agents without losing context."
  },
  {
    text: "Tip: Use visitor session replays to understand how customers navigate before requesting support."
  },
  {
    text: "Tip: Create role-based permissions so agents only access customer data relevant to their role."
  },
  {
    text: "Tip: Monitor average chat duration trends to identify efficiency gains or training needs."
  },
  {
    text: "Tip: Use proactive chat invitations to help visitors on high-bounce pages convert."
  },
  {
    text: "Tip: Set up automated responses for out-of-hours chats to manage expectations and gather info."
  },
  {
    text: "Tip: Create custom reports comparing agent performance across response time, resolution rate, and CSAT."
  },
  {
    text: "Tip: Use workflow conditions to create personalized bot experiences based on visitor segment."
  },
  {
    text: "Tip: Enable chat ratings so customers provide feedback that informs agent coaching priorities."
  },
  {
    text: "Tip: Set up automated follow-up messages to reconnect with customers on unresolved topics."
  },
  {
    text: "Tip: Use agent availability calendar to schedule shifts and prevent chat overflow during gaps."
  },
  {
    text: "Tip: Create a bot fallback response for unrecognized questions to gracefully escalate to humans."
  },
  {
    text: "Tip: Monitor first response time as a key metric that directly impacts customer satisfaction scores."
  },
  {
    text: "Tip: Use visitor attributes to trigger different chat flows based on purchase history or loyalty status."
  },
  {
    text: "Tip: Set up CRM sync to automatically log chat outcomes as customer interaction records."
  },
  {
    text: "Tip: Enable typing indicators to show customers that agents are actively composing responses."
  },
  {
    text: "Tip: Create service level agreements in reporting to track whether your team meets response targets."
  },
  {
    text: "Tip: Use conditional logic in workflows to route complex issues to senior agents automatically."
  },
  {
    text: "Tip: Monitor chat conversion rates to understand which agents drive sales or successful resolutions."
  },
  {
    text: "Tip: Set up knowledge base analytics to identify gaps where customers need more article content."
  },
  {
    text: "Tip: Use chat routing rules to automatically assign conversations based on agent skills and availability."
  },
  {
    text: "Tip: Create canned responses for common questions to reduce response time and ensure consistency."
  },
  {
    text: "Tip: Monitor your knowledge base performance in reporting to identify gaps in self-service content."
  },
  {
    text: "Tip: Set up bot handoff triggers to seamlessly pass complex issues to human agents."
  },
  {
    text: "Tip: Use customer tags in your CRM to segment visitors and personalize support interactions."
  },
  {
    text: "Tip: Enable chat transcripts in your workflow so agents can review conversation history instantly."
  },
  {
    text: "Tip: Configure SLA alerts to notify teams when response time targets are at risk of being missed."
  },
  {
    text: "Tip: Link your knowledge base articles to bot responses for customers seeking detailed information."
  },
  {
    text: "Tip: Use the conversation analytics dashboard to identify your most common support topics monthly."
  },
  {
    text: "Tip: Create separate chat queues by department to improve routing accuracy and resolution times."
  },
  {
    text: "Tip: Set up automated workflows to log customer issues directly into your CRM after chat ends."
  },
  {
    text: "Tip: Test your bot conversation flows regularly to ensure they handle edge cases and variations."
  },
  {
    text: "Tip: Review agent performance metrics weekly to recognize top performers and coach struggling team members."
  }
];

// ─── Customer service facts ───────────────────────────────────────────────────
const FACTS: WarmupMessage[] = [
  {
    text: "92% of customers rate live chat as the most satisfying support channel"
  },
  {
    text: "Live chat has the highest satisfaction rate of any support channel"
  },
  { text: "63% of millennials prefer live chat over phone or email support" },
  {
    text: "Proactive chat invitations have a 6× higher conversion rate than reactive chat"
  },
  {
    text: "Skilled agents can handle 3 to 5 simultaneous conversations at once"
  },
  { text: "Bots reduce agent workload by up to 40% on common questions" },
  {
    text: "First contact resolution is the single biggest driver of customer satisfaction"
  },
  {
    text: "Personalized support increases customer satisfaction scores by 20%"
  },
  {
    text: "44% of online consumers want a live agent available within 5 minutes"
  },
  { text: "Teams using routing rules resolve conversations 25% faster" },
  { text: "Agents who use canned responses are 35% more efficient" },
  {
    text: "Sentiment analysis can catch at-risk conversations before they escalate"
  },
  { text: "Over 60% of live chat sessions now come from mobile devices" },
  { text: "Response times under one minute increase CSAT scores by 15%" },
  {
    text: "The average live chat conversation is resolved in under 10 minutes"
  },
  { text: "79% of customers prefer live chat for quick questions" },
  { text: "Chat support costs 17–30% less to deliver than phone support" },
  { text: "Customers who use live chat are 3× more likely to make a purchase" },
  {
    text: "AI bots handle 60% of common customer questions without agent involvement"
  },
  { text: "Teams using queue alerts see 20% shorter average wait times" },
  { text: "Companies offering 24/7 support see 15% higher customer retention" },
  {
    text: "Agents with access to full customer history resolve tickets 40% faster"
  },
  {
    text: "Surveys sent immediately after a conversation have 3× higher response rates"
  },
  { text: "Teams that review CSAT weekly improve scores twice as fast" },
  {
    text: "Chatbots that hand off at the right moment increase satisfaction by 22%"
  },
  {
    text: "Omnichannel support teams retain 89% of customers vs 33% for single-channel"
  },
  { text: "Video support sessions have a 73% first-contact resolution rate" },
  { text: "Self-service options reduce ticket volume by up to 40%" },
  {
    text: "Customers who rate support 5 stars spend 140% more than those who rate 1 star"
  },
  { text: "Voice-of-the-customer programs improve retention by up to 55%" },
  {
    text: "Average hold time tolerance has dropped to 2 minutes for live chat"
  },
  { text: "Proactive support reduces inbound calls by 20–30%" },
  {
    text: "Agent empathy training improves CSAT scores by an average of 12 points"
  },
  { text: "Knowledge bases reduce inbound support volume by up to 25%" },
  {
    text: "73% of customers say fast resolution is what defines great support"
  },
  { text: "Businesses that use proactive chat see a 10–15% lift in revenue" },
  {
    text: "It costs 5× more to acquire a new customer than to retain an existing one"
  },
  { text: "A 5% increase in retention can grow profits by up to 95%" },
  { text: "86% of buyers will pay more for a better customer experience" },
  { text: "The average cost of a missed chat is a lost customer for 2 years" },
  { text: "Teams with documented processes resolve issues 30% faster" },
  { text: "66% of customers switch brands due to poor service — not price" },
  { text: "Repeat customers spend 67% more than new customers on average" },
  {
    text: "89% of companies now compete primarily on the basis of customer experience"
  },
  {
    text: "Customers who had a great service experience tell 9 people on average"
  },
  { text: "Customers who had a bad experience tell 16 people on average" },
  {
    text: "42% of service agents can't resolve issues efficiently due to disconnected systems"
  },
  {
    text: "Customer effort score predicts loyalty better than satisfaction score alone"
  },
  { text: "Reducing customer effort by 1 point improves loyalty by up to 22%" },
  {
    text: "AI-assisted agents resolve 40% more tickets per hour than unassisted agents"
  },
  {
    text: "80% of customer service interactions will be handled by AI by 2030"
  },
  {
    text: "Personalized service increases customer lifetime value by an average of 25%"
  },
  {
    text: "The top reason customers contact support is lack of self-service that works"
  },
  { text: "Proactive service prevents 20–30% of inbound support volume" },
  {
    text: "Chat bots resolve 69% of conversations end-to-end when trained on real data"
  },
  {
    text: "Teams that review and act on CSAT comments improve scores 2× faster than those that don't"
  },
  {
    text: "Knowledge base articles that are specific outperform generic ones 4 to 1 on deflection"
  },
  {
    text: "57% of customers prefer live chat for complex issues over any other channel"
  },
  {
    text: "74% of consumers use multiple channels before a final purchase decision"
  },
  {
    text: "Support teams using AI handle 35% more volume with the same headcount"
  },
  {
    text: "Routing accuracy improvements of 10% translate directly to 10% faster resolution times"
  },
  {
    text: "First-contact resolution rates above 75% correlate with retention rates above 90%"
  },
  {
    text: "Average chat handle time is 6.5 minutes — down from 9 minutes a decade ago"
  },
  {
    text: "Chat satisfaction scores are 12 points higher when agents use customer names"
  },
  {
    text: "Teams with documented escalation paths resolve P1 issues 50% faster"
  },
  {
    text: "Customers who resolve issues through self-service rate satisfaction 15% lower than live support"
  },
  {
    text: "Support agents have the highest employee churn rate of any knowledge-worker role"
  },
  {
    text: "Teams that give agents more autonomy on resolutions see 20% higher CSAT"
  }
];

// ─── Positivity & mental wellness ────────────────────────────────────────────
const POSITIVITY: WarmupMessage[] = [
  {
    text: "You can't control what others do — only how you choose to respond to it"
  },
  { text: "A three-second pause before reacting changes the outcome entirely" },
  {
    text: "Your focus is your most valuable asset — choose deliberately what gets it"
  },
  { text: "Protect your energy the same way you protect your time" },
  { text: "Other people's frustration is information, not a verdict on you" },
  {
    text: "Every difficult interaction is a skill you're building, not a test you're failing"
  },
  { text: "Setting a boundary is not unkind — it's honest and necessary" },
  {
    text: "Start the day with one small win and use that momentum all morning"
  },
  { text: "You are not required to absorb someone else's bad day" },
  {
    text: "Four slow breaths resets your nervous system faster than almost anything else"
  },
  {
    text: "The most effective people respond thoughtfully — they don't react impulsively"
  },
  {
    text: "A clear workspace reduces mental clutter more than most people expect"
  },
  {
    text: "Gratitude for small things builds the resilience to handle big ones"
  },
  {
    text: "Progress — not perfection — is the only reasonable goal for a working day"
  },
  {
    text: "Stepping away for five minutes often solves what an hour of staring cannot"
  },
  {
    text: "Difficult conversations handled calmly build more trust than easy ones"
  },
  {
    text: "Your calm in a stressful moment is the most powerful thing in the room"
  },
  { text: "Not every problem needs to be solved by you, right now, today" },
  {
    text: "Choosing to be kind costs nothing and pays back compoundly over time"
  },
  { text: "When something frustrates you, ask what it is trying to teach you" },
  { text: "Your best work comes from a settled mind, not a busy one" },
  {
    text: "Saying 'I don't know yet' is more honest and more powerful than a quick guess"
  },
  {
    text: "Energy flows where attention goes — be intentional about where yours points"
  },
  {
    text: "The day that seems overwhelming in the morning usually looks smaller by noon"
  },
  { text: "Rest is part of high performance, not the opposite of it" },
  { text: "Confidence is built one kept promise to yourself at a time" },
  {
    text: "You do not need to win every conversation — sometimes listening is the win"
  },
  { text: "What feels urgent at 9 AM rarely still feels urgent at 5 PM" },
  { text: "Your tone sets the temperature for every room you walk into" },
  {
    text: "Celebrate the small completions — they add up to something remarkable"
  },
  {
    text: "The most resilient people are not unaffected — they just recover faster"
  },
  { text: "Be the energy you would want to walk into" },
  { text: "Helping one person well today is a great day by any measure" },
  { text: "Your reaction to adversity is what others will remember about you" },
  { text: "Take pride in how you handle hard days — not just the easy ones" },
  { text: "What you practice daily is what you become eventually" },
  { text: "A gentle answer turns away frustration — tone is everything" },
  {
    text: "The version of yourself you're building right now is the one that handles tomorrow"
  },
  {
    text: "People forget what you said — they never forget how you made them feel"
  },
  {
    text: "Your value is not measured by your busiest day but by your most intentional one"
  },
  {
    text: "A bad moment does not have to become a bad day — the reset button is always available"
  },
  {
    text: "The habit of pausing before responding is worth more than any script"
  },
  { text: "Kindness under pressure is the ultimate sign of strength" },
  { text: "Tomorrow's you will thank today's you for staying calm under fire" },
  {
    text: "Small acts of consideration create cultures that people never want to leave"
  },
  {
    text: "Listening is not waiting for your turn to talk — it's genuinely trying to understand"
  },
  { text: "Focus on what you can influence and let go of what you cannot" },
  {
    text: "Give yourself credit for showing up, especially on the harder days"
  },
  {
    text: "High standards and self-compassion are not opposites — you need both"
  },
  {
    text: "The most successful people fail often — and choose to keep going anyway"
  },
  {
    text: "Ask yourself: will this matter in a year? That answer changes everything"
  },
  {
    text: "Your patience today becomes the customer's story about your company tomorrow"
  },
  {
    text: "The team that celebrates small wins together tends to stay together"
  },
  {
    text: "A kind word to a teammate costs nothing and often means everything"
  },
  {
    text: "Curiosity is more powerful than certainty — stay open to being surprised"
  },
  { text: "You bring something to this team that no one else can replicate" },
  {
    text: "When you help someone feel heard, you have already solved half their problem"
  },
  {
    text: "The tone you set at the start of a conversation determines most of how it ends"
  },
  {
    text: "Difficult days are not setbacks — they're the days that build your real capability"
  },
  {
    text: "What drains you most is worth examining — often a small change can protect a lot of energy"
  },
  {
    text: "The way you speak to yourself after a mistake matters as much as the mistake itself"
  },
  {
    text: "Patience is a form of confidence — it says 'I trust this will work out'"
  },
  {
    text: "A short walk between intense tasks resets focus better than pushing through"
  },
  {
    text: "People who thrive long-term are usually the ones who rest without guilt"
  },
  {
    text: "You are allowed to take a moment before answering — that pause is a sign of maturity"
  },
  {
    text: "Comparison is a trap — your only real benchmark is yesterday's version of yourself"
  },
  {
    text: "The hardest conversations, handled well, become the most trusted relationships"
  },
  {
    text: "Protecting your energy is not laziness — it's the foundation of sustainable effort"
  },
  {
    text: "Small moments of acknowledgment mean more to people than big gestures once a year"
  },
  {
    text: "Assume positive intent first — most misunderstandings dissolve when you do"
  },
  { text: "The strongest teams check in on each other, not just on the work" },
  { text: "Knowing when to ask for help is a skill — not a weakness" },
  {
    text: "You do not need to be perfect to be valuable — you need to be consistent and honest"
  },
  {
    text: "A day where you helped one person completely is never a wasted day"
  },
  {
    text: "The people who stay calm in chaos become the ones others quietly depend on"
  },
  {
    text: "Discomfort is usually the feeling of growing — not the feeling of failing"
  },
  { text: "The way you end a hard conversation shapes whether it stays hard" },
  {
    text: "Clarity is kindness — the clearer your communication, the less confusion you leave behind"
  },
  {
    text: "You are probably doing better than the voice in your head is willing to admit"
  },
  {
    text: "Every expert in any field was once a beginner who refused to give up"
  },
  {
    text: "Showing up consistently, even imperfectly, is more powerful than showing up brilliantly once"
  },
  {
    text: "Your mood doesn't have to be contagious — but your calm absolutely can be"
  },
  {
    text: "Saying 'I was wrong' quickly is a superpower most people never develop"
  },
  {
    text: "What you give attention to grows — choose what you feed your focus today"
  },
  {
    text: "The best version of you isn't more productive — it's more intentional"
  },
  {
    text: "A well-timed compliment to a colleague costs nothing and often changes their whole day"
  },
  {
    text: "You cannot pour from an empty cup — filling yours is not selfish, it's strategic"
  },
  {
    text: "A frustrated customer is not a personal attack — it's a problem looking for a partner."
  },
  {
    text: "You don't have to carry someone else's bad mood into your next conversation."
  },
  {
    text: "Take one slow breath between chats. It costs five seconds and resets your whole focus."
  },
  {
    text: "Progress counts even when it doesn't feel dramatic. Steady work adds up quietly."
  },
  { text: "A short walk away from your screen can undo an hour of tension." },
  {
    text: "You're allowed to feel tired and still be great at your job. Both things are true."
  },
  {
    text: "Helping one person well matters more than rushing through ten conversations poorly."
  },
  {
    text: "Your patience is a skill, not an accident. Give yourself credit for building it."
  },
  {
    text: "Difficult interactions end. The kindness you showed during them stays with people."
  },
  {
    text: "Protect your energy like a resource — small boundaries make long days sustainable."
  },
  {
    text: "Drinking water sounds too simple to help, but dehydration quietly drains your mood."
  },
  {
    text: "You set the tone for every chat you enter. That's a quiet superpower."
  },
  {
    text: "Celebrate the saves — the angry customer who left happy is a real win."
  },
  {
    text: "Not every hard conversation means something went wrong. Some problems are just hard."
  },
  {
    text: "Lending someone calm when they have none is one of the most generous things you can do."
  },
  {
    text: "End-of-day fatigue doesn't erase your morning wins. The whole day counts."
  },
  {
    text: "Asking a teammate for help isn't weakness — it's how strong teams actually work."
  },
  {
    text: "A frustrated customer is not a personal attack — it's a problem asking to be solved."
  },
  {
    text: "One rough conversation does not define your whole day. Reset and move forward."
  },
  {
    text: "Take three slow breaths between chats. Small pauses protect your energy all day."
  },
  {
    text: "You don't have to absorb someone else's stress to be empathetic toward them."
  },
  {
    text: "Celebrate the quiet wins — the customer who left calmer than they arrived."
  },
  {
    text: "Helping one person at a time is enough. You don't need to save the whole queue."
  },
  {
    text: "Step away from your screen for two minutes. Your next customer deserves fresh eyes."
  },
  {
    text: "Your patience is a skill, not a bottomless resource. Recharge it on purpose."
  },
  {
    text: "Difficult interactions build expertise. You are sharper today than you were last month."
  },
  {
    text: "A glass of water and a good stretch can shift your entire afternoon."
  },
  { text: "You set the tone of the conversation. Calm is contagious." },
  {
    text: "Not every silence needs filling. Give yourself a moment between tasks."
  },
  { text: "It's okay to ask a teammate for help. That's what teams are for." },
  {
    text: "End your shift by noting one interaction that went well. Memory shapes morale."
  },
  {
    text: "Kindness toward yourself isn't a luxury — it makes you better at your job."
  },
  {
    text: "Progress feels invisible in the moment but obvious when you look back a month."
  },
  {
    text: "Your voice matters on this team. Speak up when something could be better."
  },
  {
    text: "Taking a deep breath between calls helps reset your nervous system. You've got this."
  },
  {
    text: "Your patience with difficult customers is a real skill, not just something you're doing."
  },
  {
    text: "It's okay to feel tired after emotionally demanding conversations. That's normal and valid."
  },
  {
    text: "Small wins count. That one satisfied customer today matters more than you think."
  },
  {
    text: "Step away from your desk for five minutes when you need to. Your work will still be there."
  },
  {
    text: "Difficult interactions say nothing about your worth or abilities as a support professional."
  },
  {
    text: "You're allowed to set boundaries with customers while still being genuinely helpful."
  },
  {
    text: "Notice what you did well today, even if some calls were harder than others."
  },
  {
    text: "Drinking water and moving your body are simple ways to shift how you feel right now."
  },
  {
    text: "Every interaction you handle builds your experience and confidence, even the tough ones."
  },
  {
    text: "Your tone matters. Speaking kindly to yourself between calls matters just as much."
  },
  {
    text: "Recovery after a hard conversation takes time. Give yourself permission to regroup."
  },
  {
    text: "You don't need to be perfect to be effective at helping customers find solutions."
  },
  {
    text: "Frustration from a customer usually isn't about you. It's about their problem."
  },
  {
    text: "Your empathy is an asset, not a liability, even when it makes things feel heavier."
  },
  {
    text: "Tomorrow is a fresh start. Today's difficult moment doesn't define your week."
  },
  {
    text: "Noticing your own stress early makes it easier to manage before it builds up."
  },
  {
    text: "You're developing real skills in communication, problem-solving, and resilience."
  },
  {
    text: "It's helpful to remember that most customers are just trying to solve their problem."
  },
  {
    text: "Taking breaks isn't lazy. It's necessary maintenance for doing good work."
  },
  {
    text: "The customers who are hardest to help are often the ones who need it most."
  },
  {
    text: "Your perspective and experience bring real value to the conversations you're having."
  },
  { text: "Feeling drained after work doesn't mean you did anything wrong." },
  {
    text: "One difficult call doesn't erase the dozens of positive interactions you've had."
  },
  {
    text: "You're learning something useful from every customer interaction, even the rough ones."
  },
  {
    text: "Checking in with yourself about how you're feeling is a strength, not weakness."
  },
  {
    text: "Your job has real impact. People actually appreciate the help you're giving them."
  },
  {
    text: "Negative feedback usually contains one useful thing and a lot of emotion. Both are okay."
  },
  {
    text: "You're allowed to be human at work, including having tough moments and bad days."
  },
  {
    text: "Progress looks like handling the same situation better the second time around."
  },
  {
    text: "Your mental health during work is just as important as getting through your queue."
  },
  {
    text: "Listening to customers carefully is genuinely difficult work. You're doing it well."
  },
  {
    text: "Mistakes happen. They're part of learning, not proof that you're not good enough."
  },
  {
    text: "You can be kind to a customer and still protect your own peace of mind."
  },
  { text: "The skills you're building here apply far beyond this job." },
  {
    text: "Sometimes just making it through the day is an accomplishment. Own that."
  },
  {
    text: "Curious about why a customer is upset often helps you understand them better."
  },
  {
    text: "Your energy and mood matter. Taking care of yourself is part of great customer service."
  },
  {
    text: "Walking away from your computer for a moment costs nothing and helps everything."
  },
  {
    text: "The best support comes from people who also know how to take care of themselves."
  },
  {
    text: "You're more resilient than you realize, especially after handling a tough situation."
  },
  {
    text: "Today's hard moment is tomorrow's experience that will help you handle something better."
  },
  {
    text: "Being affected by what you hear shows you care, and that's actually a good quality."
  },
  {
    text: "You get to decide how you talk to yourself. Make it kind whenever you can."
  },
  {
    text: "Recovery isn't weakness. It's how you keep showing up the way you want to."
  },
  {
    text: "Your contribution to a customer's day might matter more than you'll ever know."
  },
  {
    text: "Stress is normal in this role. How you respond to it is what makes the difference."
  },
  {
    text: "You don't have to be cheerful every moment to be good at your job."
  },
  {
    text: "Notice the moments when things go smoothly. Those matter just as much as the hard ones."
  },
  {
    text: "Supporting others is meaningful work, and it's okay to feel the weight of it sometimes."
  },
  {
    text: "Taking a few deep breaths between conversations can reset your nervous system and help you show up better."
  },
  {
    text: "It's okay to feel tired after a difficult chat. That means you cared enough to really listen."
  },
  {
    text: "Your tone sets the temperature of the room. When you're calm, customers feel safer too."
  },
  {
    text: "Frustration from customers is rarely about you personally. Try to see the person behind the problem."
  },
  {
    text: "Small wins count. Helping one person solve their issue is a meaningful day's work."
  },
  {
    text: "Notice when you've handled something well. Let yourself feel good about those moments."
  },
  {
    text: "Stepping away for five minutes isn't avoiding work—it's protecting your ability to do it well."
  },
  {
    text: "Customers appreciate honesty more than perfection. It's okay to say what you don't know."
  },
  {
    text: "Your empathy is a skill, not a weakness. The best support comes from genuinely caring."
  },
  {
    text: "Difficult conversations get easier when you remember you're both trying to solve something together."
  },
  {
    text: "You don't need to have all the answers to be helpful. Sometimes listening is enough."
  },
  {
    text: "Building rapport takes time. Be patient with yourself and your customers as you both adjust."
  },
  {
    text: "When a conversation feels stuck, a genuine question often helps more than another explanation."
  },
  {
    text: "Rest is productive. You can't pour from an empty cup, and your customers deserve your best."
  },
  {
    text: "Staying curious about why customers are frustrated helps you help them better."
  },
  {
    text: "Your boundaries matter. It's healthy to protect your energy so you can show up consistently."
  },
  {
    text: "Mistakes happen to everyone. What matters is how you handle them and move forward."
  },
  {
    text: "Connecting with one customer deeply is better than rushing through many shallow interactions."
  },
  {
    text: "Notice your own stress signals early. Small adjustments prevent bigger burnout later."
  },
  {
    text: "You're learning something valuable every day, even when it doesn't feel like it."
  },
  {
    text: "Being present with someone's frustration, without trying to fix it immediately, builds trust."
  },
  {
    text: "Your job matters. The work you do genuinely improves someone's day on the other end."
  },
  {
    text: "Perfectionism is the enemy of progress. Good enough, delivered with care, is actually perfect."
  },
  {
    text: "When you feel defensive, pause. That feeling is information about what matters to you."
  },
  {
    text: "Celebrating small improvements, not just solutions, keeps motivation alive."
  },
  {
    text: "Customers remember how you made them feel long after they forget the technical details."
  },
  {
    text: "Setting expectations early prevents a lot of frustration later for both of you."
  },
  { text: "You're allowed to have bad days. Tomorrow is a fresh start." },
  {
    text: "Finding humor in complicated situations isn't dismissive—it's a healthy coping tool."
  },
  {
    text: "Acknowledging what you don't know builds more credibility than pretending you know everything."
  },
  {
    text: "Your tone in writing matters just as much as your voice on calls. Warmth translates everywhere."
  },
  {
    text: "Recovery time between difficult interactions is not lost time—it's essential maintenance."
  },
  {
    text: "Being genuinely interested in solving the problem, not winning the argument, changes everything."
  },
  {
    text: "Small acts of kindness in support interactions create ripples you'll never fully see."
  },
  {
    text: "Fatigue makes everything harder. Stay hydrated, take lunch breaks, and actually rest."
  },
  {
    text: "When conversations feel personal, remember: you're interacting with someone's frustration, not their judgment of you."
  },
  {
    text: "Progress isn't always visible. Trust that your consistent effort is making a real difference."
  },
  {
    text: "Asking clarifying questions shows respect and prevents misunderstandings before they compound."
  },
  {
    text: "Your vulnerability—admitting when you need help—actually strengthens your relationships with customers."
  },
  {
    text: "Negative feedback about a situation isn't feedback about your worth as a person."
  },
  {
    text: "Bringing your authentic self to work, within professional boundaries, creates better connections."
  },
  {
    text: "Sometimes the most helpful thing you can do is acknowledge that the situation is genuinely frustrating."
  },
  {
    text: "Your energy is contagious. Protecting your own wellbeing helps your whole team."
  },
  {
    text: "Difficult customers often just need to feel heard before they can move forward."
  },
  {
    text: "Consistency matters more than intensity. Showing up regularly builds trust deeper than heroic efforts."
  },
  {
    text: "You can be compassionate and firm at the same time. Both serve the customer better."
  },
  {
    text: "Growth comes from reflection, not rumination. Learn from hard interactions, then let them go."
  },
  {
    text: "Being kind to yourself when you mess up models the grace you extend to frustrated customers."
  },
  {
    text: "Every resolved conversation is proof that you know how to do this, even when it feels impossible."
  },
  {
    text: "Balance isn't something you find—it's something you build, day by day, choice by choice."
  },
  {
    text: "Taking a few deep breaths between calls helps reset your nervous system and keeps you grounded."
  },
  {
    text: "It's okay to feel tired after a difficult conversation. That means you cared about helping."
  },
  {
    text: "Your tone matters more than your words. Slow down and let warmth come through naturally."
  },
  {
    text: "When a customer is upset, they're usually frustrated with the situation, not with you personally."
  },
  {
    text: "Drink some water. Small acts of self-care compound into better energy throughout your day."
  },
  {
    text: "You don't need to have all the answers. Sometimes listening is the most valuable thing you offer."
  },
  {
    text: "Notice one thing you did well today, no matter how small. Build from there tomorrow."
  },
  {
    text: "Difficult interactions teach you more than easy ones. You're growing even on tough days."
  },
  {
    text: "Set a boundary between work stress and personal time. Your brain needs permission to rest."
  },
  {
    text: "Kindness to yourself matters as much as kindness to customers. You deserve that same care."
  },
  {
    text: "If a conversation didn't go as planned, it's data for improvement, not proof you're failing."
  },
  {
    text: "Your emotional labor is real work. Acknowledge the effort you're putting in each day."
  },
  {
    text: "Taking a walk between shifts helps clear your mind and resets your energy for the next one."
  },
  {
    text: "Not every customer will be easy. That's normal, and it doesn't reflect your ability to help."
  },
  {
    text: "Celebrate the wins—solved problems, happy customers, smooth conversations. They add up."
  },
  {
    text: "Your past interactions don't determine your next one. Each conversation is a fresh start."
  },
  {
    text: "Feeling overwhelmed sometimes means you care about doing good work. That's a strength."
  },
  {
    text: "Smile before you pick up. It genuinely changes how your voice sounds and feels."
  },
  {
    text: "Progress isn't about perfection. Small improvements in patience compound into real change."
  },
  {
    text: "You bring something unique to every interaction. Trust your instincts and your experience."
  },
  {
    text: "If you need a moment, take it. A few minutes of quiet helps you show up better for others."
  },
  {
    text: "Mistakes happen. What matters is what you learn and how you move forward from them."
  },
  {
    text: "Your customers benefit from your experience. You know more than you think you do."
  },
  {
    text: "Some days are harder than others. That's completely normal and doesn't mean you're doing it wrong."
  },
  {
    text: "The patience you build here transfers to your whole life. You're developing a real skill."
  },
  {
    text: "Notice when you helped someone feel heard. That moment matters more than you realize."
  },
  {
    text: "Frustration is information. It tells you where you might need support or a different approach."
  },
  {
    text: "You don't have to be perfect at this. Being genuine and trying your best is enough."
  },
  {
    text: "Rest isn't lazy. It's how you recover and show up stronger for the next interaction."
  },
  {
    text: "Your perspective changes as you gain experience. Trust the process of learning over time."
  },
  {
    text: "If a customer was difficult, it usually has nothing to do with your worth or ability."
  },
  {
    text: "Small moments of connection with customers are the real reward of this work."
  },
  {
    text: "You're allowed to feel things. Let yourself process emotions instead of pushing them away."
  },
  {
    text: "Tomorrow is a new day with new interactions. Today's challenges don't define you."
  },
  {
    text: "Taking feedback seriously while not taking it personally is a skill worth developing."
  },
  {
    text: "Your consistency matters more than your perfection. Show up and do your best each day."
  },
  {
    text: "Helping people often means being their first good interaction in a difficult situation."
  },
  {
    text: "Notice the patterns in what drains you. Understanding them helps you manage your energy."
  },
  {
    text: "You're developing emotional resilience even on days that feel hard. Growth isn't linear."
  },
  {
    text: "A customer's anger is about their problem, not about you. Keep that boundary clear."
  },
  {
    text: "Talking through a tough interaction with a coworker helps process it and move forward."
  },
  {
    text: "Your effort to understand customers creates real value, even when it's not obvious."
  },
  {
    text: "Gratitude for small things—a smooth call, a kind customer, lunch—builds your reserves."
  },
  {
    text: "You don't need permission to take care of yourself. Your wellbeing enables better work."
  },
  {
    text: "Patience is a skill you're building here. It makes everything in your life better."
  },
  {
    text: "When you're struggling, that's often when you're learning the most. Trust the process."
  },
  {
    text: "Your voice and perspective matter. You bring something irreplaceable to your role."
  },
  {
    text: "Stress from work doesn't define your capability. You're stronger than one bad interaction."
  },
  {
    text: "Finding humor in difficult situations helps you stay sane and connected to yourself."
  },
  {
    text: "You've gotten through every hard day so far. You have more strength than you think."
  },
  {
    text: "Taking a deep breath after a tough call? That's self-awareness, and it's a superpower."
  },
  {
    text: "Your patience with customers is a skill you're building, not something you're born with."
  },
  {
    text: "Feeling drained? Short breaks between interactions can restore your focus and empathy."
  },
  {
    text: "One difficult conversation doesn't define your ability to help others."
  },
  { text: "Notice what you did well today, even in the challenging moments." },
  {
    text: "Stress is just feedback that something matters to you. You're doing meaningful work."
  },
  {
    text: "Your tone sets the temperature for the entire conversation. Slow down when you need to."
  },
  {
    text: "Reading the room and adjusting your approach shows real emotional intelligence."
  },
  {
    text: "It's okay to not have all the answers right now. Learning is part of the job."
  },
  {
    text: "Customers remember how you made them feel more than what you said."
  },
  {
    text: "Taking care of your mental energy is as important as taking care of your tasks."
  },
  {
    text: "Progress looks like staying calm when things get heated. You're getting better at this."
  },
  { text: "Difficult interactions often teach us the most about ourselves." },
  {
    text: "Your voice matters, and so does your wellbeing. Both are equally important."
  },
  { text: "Setting boundaries with your work isn't selfish—it's sustainable." },
  {
    text: "Some days will feel harder than others. That's completely normal and temporary."
  },
  {
    text: "Listening deeply to customers, even frustrated ones, builds real connection."
  },
  { text: "You don't need permission to take a moment and reset." },
  { text: "Mistakes in customer interactions are data, not character flaws." },
  { text: "Small wins add up. Celebrate helping even one person today." },
  {
    text: "Your nervous system needs recovery time. Honor that need without guilt."
  },
  {
    text: "Empathy isn't about agreeing with everything—it's about understanding."
  },
  {
    text: "Walking away from your desk for five minutes is productivity, not procrastination."
  },
  {
    text: "You're building resilience every time you stay present with a difficult customer."
  },
  {
    text: "Frustration is information. It's telling you something needs adjustment."
  },
  {
    text: "Your value isn't determined by your response time or customer satisfaction score."
  },
  {
    text: "Recovery from hard interactions starts with acknowledging they were actually hard."
  },
  {
    text: "Staying grounded means noticing what's real right now, not spiraling ahead."
  },
  {
    text: "Good communication is messy sometimes. That's where real progress happens."
  },
  { text: "You're allowed to feel tired and still be good at your job." },
  { text: "Asking for help or support is a sign of strength, not weakness." },
  {
    text: "Each conversation is new. You don't carry the last one into this one."
  },
  { text: "Your instincts about customer needs are usually worth trusting." },
  {
    text: "Positive work culture starts with you taking care of yourself first."
  },
  { text: "Notice patterns in your stress, not just the stress itself." },
  {
    text: "Difficult customers often need help more than they need to be right."
  },
  { text: "Your well-being directly impacts the support you can give." },
  {
    text: "Compassion fatigue is real. Naming it is the first step to managing it."
  },
  {
    text: "You're developing something valuable: the ability to stay calm under pressure."
  },
  { text: "Sometimes the best support is simply being present, not fixing." },
  {
    text: "Your perspective matters even when customers don't immediately see it."
  },
  { text: "Managing your expectations helps you manage your stress." },
  {
    text: "Being supportive doesn't mean absorbing other people's negativity."
  },
  { text: "Growth happens in the moments that feel uncomfortable." },
  { text: "You don't have to be perfect to be genuinely helpful." },
  {
    text: "Checking in with yourself is as important as checking in with customers."
  },
  { text: "Small acts of patience compound into professional confidence." },
  {
    text: "Your nervous system is learning to handle pressure better each day."
  },
  { text: "Sustainable support means protecting your own emotional reserves." },
  {
    text: "Today's challenging conversation might be someone's turning point. That matters."
  },
  {
    text: "Taking a few deep breaths after a tough call isn't weakness—it's how you show up better for the next one."
  },
  {
    text: "Your value isn't determined by one difficult conversation. You're building skills every single day."
  },
  {
    text: "It's okay to feel frustrated. Acknowledging it helps you move through it faster."
  },
  {
    text: "Drink some water. Stretch for a moment. Small acts of care compound into real resilience."
  },
  {
    text: "That customer wasn't upset with you personally. Their frustration is about their problem, not your worth."
  },
  {
    text: "You don't have to be perfect. Good enough, done with care, is actually excellent."
  },
  {
    text: "Notice one thing you handled well today, no matter how small. That's progress worth recognizing."
  },
  {
    text: "Stepping away for five minutes isn't procrastination—it's maintenance for your focus."
  },
  {
    text: "Your empathy is a strength, even when it's exhausting. Especially then."
  },
  {
    text: "Hard interactions teach you more than easy ones. You're becoming more skilled right now."
  },
  {
    text: "Feeling drained is normal after emotional labor. That's your signal to refill your cup."
  },
  {
    text: "You've handled difficult moments before. You have more capability than you probably realize."
  },
  {
    text: "One frustrated customer doesn't define your abilities or your day. Keep perspective."
  },
  {
    text: "Your patience has limits, and respecting those limits makes you more effective, not less."
  },
  { text: "Progress looks like learning from today, not being perfect in it." },
  { text: "Take a walk if you can. Movement helps reset both body and mind." },
  { text: "You're allowed to set boundaries without feeling guilty about it." },
  {
    text: "That interaction was hard because you care about doing it well. That's actually your superpower."
  },
  {
    text: "Tomorrow is a fresh start. Today doesn't carry over unless you decide it does."
  },
  {
    text: "Your team understands. Everyone here has had a tough call. You're not alone in this."
  },
  {
    text: "Listening deeply to someone's frustration is hard work. Honor yourself for doing it."
  },
  {
    text: "You can't control how people react, only how thoughtfully you respond. You're doing the hard part."
  },
  { text: "Rest is part of being good at your job, not taking away from it." },
  {
    text: "Notice your wins, even the quiet ones. They matter more than you think."
  },
  {
    text: "Feeling overwhelmed means you care. Channel that into one manageable task."
  },
  {
    text: "You've recovered from hard days before. You have the strength to do it again."
  },
  {
    text: "Being kind to a frustrated person costs you something real. Acknowledge that effort."
  },
  {
    text: "A bad call doesn't make you bad at your job. It makes you human doing a human job."
  },
  { text: "You deserve the same patience and grace you give to customers." },
  {
    text: "Stress is feedback that something matters to you. That's not a flaw—that's meaning."
  },
  {
    text: "Take the win, even if it's just getting through the day without losing your cool."
  },
  {
    text: "Your ability to stay calm under pressure is a rare and valuable skill."
  },
  {
    text: "Negative thoughts after a hard interaction are normal. They don't have to define your next one."
  },
  {
    text: "Small moments of peace throughout the day add up to big improvements."
  },
  {
    text: "You're developing emotional resilience. That's real, tangible growth happening right now."
  },
  {
    text: "It's not about being perfect. It's about showing up as genuinely as you can."
  },
  {
    text: "That difficult customer might not realize how much thought you put into helping them."
  },
  {
    text: "Your well-being matters as much as your performance metrics. Actually, it affects your metrics."
  },
  {
    text: "Frustration is energy. Redirect it toward something within your control."
  },
  {
    text: "You're learning what works and what doesn't. That's the whole point of experience."
  },
  {
    text: "Hard conversations get easier with practice, even when they never feel easy."
  },
  { text: "Your calmness in chaos is more valuable than you probably think." },
  {
    text: "Recovery time is productive time. Let yourself reset without guilt."
  },
  {
    text: "You handled that better than you think you did. Perspective shifts when you're calm."
  },
  {
    text: "Feeling tired after focused emotional work is proof you're doing it right."
  },
  {
    text: "One rough interaction doesn't erase the thoughtfulness you bring every other day."
  },
  {
    text: "You're exactly the kind of person who should be doing this work. Trust that."
  },
  {
    text: "Tomorrow brings fresh opportunities. This difficult moment is temporary."
  },
  {
    text: "Your resilience isn't tested on easy days. You're building real strength right now."
  },
  {
    text: "Being affected by hard interactions shows you have integrity. That's admirable."
  },
  {
    text: "Take a breath. That difficult customer interaction doesn't define your ability to help."
  },
  {
    text: "Your empathy is a strength, not a weakness. It's what makes you good at this."
  },
  {
    text: "Feeling tired after a tough call? That's normal. Rest is part of the job, not a failure."
  },
  {
    text: "You don't need to have all the answers. Listening well is often enough."
  },
  {
    text: "Small wins count. That one customer you helped today? That mattered."
  },
  {
    text: "It's okay to feel frustrated. Notice it, and let it pass without judgment."
  },
  {
    text: "Taking a short break between chats isn't lazy—it's essential maintenance."
  },
  {
    text: "You're allowed to set boundaries. Protecting your energy helps you help others better."
  },
  {
    text: "That customer's mood probably isn't about you, even if it feels personal."
  },
  {
    text: "Progress looks like showing up, even on the days you don't feel like it."
  },
  {
    text: "Your well-being matters as much as any customer satisfaction metric."
  },
  {
    text: "One bad interaction doesn't erase the good ones you've had this week."
  },
  { text: "Mistakes happen. What matters is that you're here, trying again." },
  { text: "Slow responses are better than frustrated ones. Take your time." },
  { text: "You're doing better than you think. Trust that." },
  { text: "Kindness to yourself makes you better at being kind to others." },
  {
    text: "It's okay to not be okay sometimes. That's human, not unprofessional."
  },
  { text: "Remember: you can't pour from an empty cup. Fill yours first." },
  {
    text: "That customer who was rude? They're probably struggling with something."
  },
  { text: "Your perspective matters. You see things others might miss." },
  { text: "Stress is your body trying to help. Thank it and move forward." },
  {
    text: "You've gotten through every hard day so far. You'll get through this one too."
  },
  { text: "Asking for help isn't giving up—it's working smart." },
  {
    text: "Your tone of voice carries more weight than you realize. Use it wisely."
  },
  {
    text: "Ending your shift is not abandoning anyone. It's respecting yourself."
  },
  {
    text: "Patterns get easier with time. Today feels hard because you're still learning."
  },
  {
    text: "You're allowed to not engage with every problem as if it's a crisis."
  },
  { text: "Notice what you did right today, not just what went wrong." },
  { text: "Being present is enough. You don't have to be perfect." }
];

// ─── Productivity ─────────────────────────────────────────────────────────────
const PRODUCTIVITY: WarmupMessage[] = [
  { text: "If a task takes less than two minutes, do it immediately" },
  {
    text: "Start your day with your hardest task — it makes everything else feel easier"
  },
  {
    text: "Batch similar tasks together to eliminate the cost of context switching"
  },
  { text: "Time-block your calendar — unscheduled time becomes reactive time" },
  { text: "Done is better than perfect — ship it, then iterate" },
  {
    text: "Review your three priorities for the day before opening your messages"
  },
  {
    text: "The best system is the one you will actually use consistently every day"
  },
  {
    text: "Writing things down immediately frees your brain for thinking, not storing"
  },
  { text: "End every meeting with clear next steps and a named owner" },
  { text: "One focused hour beats three scattered ones every time" },
  { text: "Saying no to a good thing creates space for a great one" },
  {
    text: "Templates and checklists eliminate the overhead of reinventing routine tasks"
  },
  {
    text: "Review what you completed today — not just what is still left on the list"
  },
  {
    text: "The five-minute rule: if you're avoiding something, just start for five minutes"
  },
  { text: "Energy management matters as much as time management" },
  { text: "Ask yourself: is this the highest-value use of my next hour?" },
  { text: "A weekly review catches what daily work consistently misses" },
  { text: "Clarity on priorities makes every decision noticeably faster" },
  {
    text: "Automation handles the repetitive so your team focuses on what matters"
  },
  {
    text: "The best support teams document everything so nothing is lost in transition"
  },
  {
    text: "Measure what you want to improve — then actually review it regularly"
  },
  { text: "Short daily standups prevent long weekly problems from forming" },
  { text: "Small process improvements compound dramatically over months" },
  {
    text: "A clean handoff between shifts is the mark of a high-performing team"
  },
  { text: "Data without a decision attached is just noise" },
  {
    text: "A strong morning routine removes decisions when willpower is naturally lowest"
  },
  {
    text: "The fastest way to unblock a project is often a five-minute conversation"
  },
  { text: "Automate anything you do more than once a week" },
  { text: "Build the habit first — optimize it after it is stable" },
  {
    text: "Great documentation is the best gift you can give your future self"
  },
  { text: "Schedule time to think — it is real work, not a luxury" },
  {
    text: "Work expands to fill the time you give it — set a deadline even for yourself"
  },
  {
    text: "Track three wins every week — the pattern shows you what to do more of"
  },
  {
    text: "Consistent beats intense — show up every day and let the gains compound"
  },
  {
    text: "The most productive people are ruthless about what they deprioritize"
  },
  {
    text: "Decisions made slowly are often made twice — build a framework and trust it"
  },
  {
    text: "If two people are doing the same work, that is a process problem to fix"
  },
  {
    text: "The 80/20 rule applies everywhere — find the 20% that drives 80% of your results"
  },
  {
    text: "A clear 'done' definition prevents tasks from dragging on indefinitely"
  },
  {
    text: "Minimize meetings — protect deep work blocks like they're appointments"
  },
  { text: "Every recurring problem is a system to fix, not a fire to fight" },
  {
    text: "Close browser tabs you're 'saving for later' — you won't go back to them"
  },
  {
    text: "Teach your process to someone else — you'll find the gaps immediately"
  },
  {
    text: "Inbox zero is less about zero emails and more about zero open decisions"
  },
  {
    text: "The best productivity hack is saying no to things that don't move the needle"
  },
  { text: "A 15-minute planning session saves hours of reactive scrambling" },
  {
    text: "Document decisions when they're made — not after people forget why"
  },
  {
    text: "A good checklist eliminates the cognitive overhead of remembering every step"
  },
  {
    text: "If you can delegate something to 80% of your quality, delegate it and move on"
  },
  {
    text: "The best teams have fewer meetings because they have better async communication"
  },
  {
    text: "Before starting a task, write down what 'done' looks like — clarity before effort"
  },
  {
    text: "Batch your email and message checking — reactive reading destroys deep work"
  },
  { text: "A daily shutdown ritual helps your brain leave work at work" },
  {
    text: "Write tomorrow's priority list today — future you will thank you for it"
  },
  {
    text: "Context-switching costs 20 minutes of focus each time — protect your unbroken blocks"
  },
  {
    text: "The most scalable thing you can do is teach your process, not just do the work"
  },
  {
    text: "Clear labels and naming conventions save hours of confusion downstream"
  },
  { text: "Small, daily investments in documentation pay dividends for years" },
  {
    text: "Ask 'what would make this easier?' before diving in — simple changes often exist"
  },
  {
    text: "Track your energy levels for a week — you'll find a clear pattern worth protecting"
  },
  {
    text: "The real cost of a bad habit is not the habit itself — it's what it crowds out"
  },
  {
    text: "Good defaults remove the need for willpower — design your environment to help you"
  },
  {
    text: "Revisit your recurring tasks regularly — some of them can just be stopped entirely"
  },
  {
    text: "The fastest way to get unstuck is to talk it through with someone else out loud"
  },
  {
    text: "Speed of decision matters more than perfection of decision in most situations"
  },
  {
    text: "Ship, learn, iterate — the cycle beats planning in isolation every time"
  },
  {
    text: "Protect the first 90 minutes of your day like they're the most valuable hours — because they are"
  },
  {
    text: "When in doubt, do the scariest task first — everything after it feels manageable"
  },
  {
    text: "Good judgment is knowing when to break the rule, not just knowing the rule"
  },
  {
    text: "The best teams celebrate both wins AND lessons — safety to fail fast is a competitive advantage"
  },
  {
    text: "Simplicity is a feature — every unnecessary complexity is a future maintenance cost"
  },
  {
    text: "Batch similar tasks together so your brain isn't constantly switching gears."
  },
  {
    text: "Write tomorrow's priority list before you close your laptop today."
  },
  {
    text: "If a task takes under two minutes, do it now instead of adding it to a list."
  },
  {
    text: "Close tabs you aren't using — visual clutter slows decisions more than you think."
  },
  {
    text: "Block thirty minutes of uninterrupted focus time and guard it like a meeting."
  },
  {
    text: "Use templates for repetitive emails so your energy goes to the work that needs thinking."
  },
  {
    text: "Review your workflow once a month and remove one step that no longer adds value."
  },
  {
    text: "Set specific check-in times for messages instead of reacting to every notification."
  },
  {
    text: "Start your day with the task you're most likely to avoid — momentum follows."
  },
  {
    text: "Keep a running doc of wins and fixes so weekly reports write themselves."
  },
  {
    text: "Say no to one low-value meeting this week and use that time to build something."
  },
  {
    text: "Organize your shortcuts and bookmarks — ten seconds saved fifty times a day adds up."
  },
  {
    text: "Name your files clearly the first time. Future-you will be grateful."
  },
  {
    text: "Finish one thing before starting another. Partial work piles up faster than you expect."
  },
  { text: "Automate any report you pull more than twice a month." },
  {
    text: "Put your phone in another room for your focus block — out of sight works."
  },
  {
    text: "Batch similar tasks together — context switching quietly drains your best hours."
  },
  { text: "Write tomorrow's priority list before you log off today." },
  {
    text: "Close tabs you aren't using right now. Visual clutter slows mental processing."
  },
  {
    text: "If a task takes under two minutes, do it now instead of adding it to your list."
  },
  {
    text: "Block a 30-minute window each day for work that requires deep focus."
  },
  {
    text: "Review your canned responses monthly and retire the ones nobody uses."
  },
  { text: "Turn off non-essential notifications during peak chat hours." },
  {
    text: "Use templates for internal handoff notes so nothing important gets lost."
  },
  {
    text: "Set a specific time to check email instead of monitoring it all day."
  },
  {
    text: "Organize your shortcuts and bookmarks — seconds saved per task compound fast."
  },
  {
    text: "After every meeting, write down the one action item that matters most."
  },
  {
    text: "Standardize your file naming so future-you can find things in seconds."
  },
  {
    text: "Identify your peak energy hours and protect them for your hardest work."
  },
  {
    text: "Automate any step you find yourself repeating more than three times a week."
  },
  {
    text: "Keep a running doc of solved edge cases — it becomes your personal playbook."
  },
  {
    text: "Start each shift by scanning the queue and setting a realistic goal for the hour."
  },
  {
    text: "Batch your customer responses into focused time blocks to minimize context switching."
  },
  {
    text: "Set a daily priority limit of three goals to avoid diluting your attention."
  },
  {
    text: "Use the two-minute rule: handle quick support tickets immediately instead of adding them to your queue."
  },
  {
    text: "Schedule deep work blocks before checking messages to protect your concentration."
  },
  {
    text: "Break complex customer issues into smaller subtasks to make progress feel tangible."
  },
  {
    text: "Review your most common customer questions and create templates to save 15+ minutes daily."
  },
  {
    text: "Start your day with your hardest customer problem, not your easiest one."
  },
  {
    text: "Turn off notifications during response-writing sessions to maintain thought clarity."
  },
  {
    text: "Identify your peak energy hours and assign your most demanding support work then."
  },
  {
    text: "Use time blocking to separate customer communication from internal administrative tasks."
  },
  {
    text: "Create a pre-shift routine that gets you into support mode faster each day."
  },
  {
    text: "Prioritize customer issues by impact and urgency, not by when they arrived."
  },
  {
    text: "Set boundaries on response time expectations to reduce unnecessary rush and errors."
  },
  {
    text: "Take short walking breaks between customer interactions to reset your focus."
  },
  {
    text: "Group similar ticket types together to build momentum and reduce mental switching costs."
  },
  {
    text: "Document your decision-making process for common issues to speed up future resolutions."
  },
  {
    text: "Eliminate decision fatigue by standardizing your approach to routine customer requests."
  },
  {
    text: "Timebox email responses to prevent one conversation from derailing your entire afternoon."
  },
  {
    text: "Use keyboard shortcuts and saved responses to automate repetitive communication patterns."
  },
  {
    text: "Plan your week on Monday morning before reactive work fills your calendar."
  },
  {
    text: "Separate incoming tickets into channels so urgent issues don't get lost in volume."
  },
  {
    text: "Take a five-minute planning break every 90 minutes to recalibrate your next priority."
  },
  {
    text: "Keep a running list of recurring customer pain points to identify systemic improvements."
  },
  {
    text: "Front-load your day with customer work before internal meetings drain your energy."
  },
  {
    text: "Use templates for common scenarios but personalize the opening to show genuine attention."
  },
  {
    text: "Block calendar time for deep work before letting meetings get scheduled into those slots."
  },
  {
    text: "Review closed tickets weekly to spot patterns that could prevent future repeat issues."
  },
  {
    text: "Set a specific cutoff time for checking support channels to protect your evening."
  },
  {
    text: "Assign each customer inquiry a single owner to eliminate confusion and duplicate effort."
  },
  {
    text: "Create a swipe file of your best responses to build on them rather than starting fresh."
  },
  {
    text: "Practice saying no to scope creep within individual tickets to stay efficient."
  },
  {
    text: "Use a inbox-zero approach for support tickets to prevent accumulated decision load."
  },
  {
    text: "Identify your three most time-consuming issue types and develop streamlined solutions."
  },
  {
    text: "Schedule customer communication in clusters rather than spreading it throughout the day."
  },
  {
    text: "Keep your support dashboard visible to catch urgent issues without obsessive checking."
  },
  {
    text: "Establish a clear handoff process when escalating issues to avoid dropped context."
  },
  {
    text: "Batch similar questions and send comprehensive answers to reduce back-and-forth cycles."
  },
  {
    text: "Start each ticket by restating the customer's problem to ensure you understood correctly."
  },
  {
    text: "Create checklists for multi-step support processes to prevent missed details under pressure."
  },
  {
    text: "Reduce decision complexity by establishing clear criteria for common support scenarios."
  },
  {
    text: "Use canned responses as starting points, not final answers, to save time without seeming automated."
  },
  {
    text: "Schedule weekly reviews of your support processes to identify friction points worth fixing."
  },
  {
    text: "Protect your last hour of the day for admin tasks instead of accepting new complex issues."
  },
  {
    text: "Create a priority system where one red-flag indicator fast-tracks issues to resolution."
  },
  {
    text: "Minimize context switching by handling all messages from one customer consecutively."
  },
  {
    text: "Set response time targets that are ambitious but realistic for your team's capacity."
  },
  {
    text: "Document solutions immediately after resolving complex issues while details are fresh."
  },
  {
    text: "Use data on your busiest hours to adjust staffing and reduce individual overwhelm."
  },
  {
    text: "Build a five-minute shutdown routine that clears your queue and preps tomorrow's focus."
  },
  {
    text: "Batch your support tickets by category to reduce context switching and boost resolution speed."
  },
  {
    text: "Set a timer for deep work blocks—even 25 minutes of uninterrupted focus improves decision quality."
  },
  {
    text: "Review your top time-wasters this week, then eliminate or delegate just one tomorrow."
  },
  {
    text: "Prioritize tickets by customer lifetime value, not just urgency, to focus on what matters most."
  },
  {
    text: "Write down your three most important support outcomes before checking messages each morning."
  },
  {
    text: "Close unnecessary browser tabs and notifications—they cost you 23 minutes of refocus per interruption."
  },
  {
    text: "Schedule response time blocks instead of reacting constantly throughout the day."
  },
  {
    text: "Document your solutions while handling tickets to build a knowledge base that saves future time."
  },
  {
    text: "Break large customer issues into smaller steps rather than solving everything at once."
  },
  {
    text: "Use templates for repetitive responses, but personalize the first line to show genuine attention."
  },
  {
    text: "Identify your peak energy hours and protect them for complex customer conversations."
  },
  {
    text: "Say no to low-impact requests so you have capacity for high-value customer relationships."
  },
  {
    text: "End each shift by listing what worked well—patterns reveal your most productive practices."
  },
  {
    text: "Delegate routine follow-ups so you focus energy on problem-solving and relationship building."
  },
  {
    text: "Single-task during customer calls instead of handling email to reduce errors and build trust."
  },
  {
    text: "Create a 'parking lot' list for questions that arise mid-support so you stay focused."
  },
  {
    text: "Review metrics weekly to spot which types of issues consume most time, then optimize them."
  },
  {
    text: "Respond to complex tickets when fresh, not at end-of-day when mental energy is depleted."
  },
  {
    text: "Use keyboard shortcuts for your most-used actions—they save 10+ minutes daily over time."
  },
  {
    text: "Ask customers clarifying questions upfront to avoid multiple back-and-forths."
  },
  {
    text: "Block calendar time for training so support quality improves and you work faster long-term."
  },
  {
    text: "Measure resolution time per ticket type to find where better processes save the most hours."
  },
  {
    text: "Avoid perfectionism on low-stakes responses—good enough delivered fast beats perfect delayed."
  },
  {
    text: "Group similar problems together and solve them in sequence to leverage your fresh expertise."
  },
  {
    text: "Read ticket subjects carefully before opening—many questions answer themselves with this habit."
  },
  {
    text: "Set expectations upfront about response times so customers don't expect instant replies."
  },
  {
    text: "Reduce meeting time by using asynchronous updates, freeing focus for actual support work."
  },
  {
    text: "Capture common questions in searchable formats so you spend less time on repetitive answers."
  },
  {
    text: "Prioritize new customer issues over repeat issues to maximize retention and lifetime value."
  },
  {
    text: "Take a 5-minute break between intense support blocks to reset attention and prevent burnout."
  },
  {
    text: "Keep a swipe file of your best solutions to reference quickly without rebuilding from scratch."
  },
  {
    text: "Automate escalation routing so trivial decisions don't interrupt your focused support time."
  },
  {
    text: "Review one customer conversation daily to identify where you can communicate more clearly."
  },
  {
    text: "Use smart filters to surface only tickets that need your specific expertise or judgment."
  },
  {
    text: "Batch similar ticket types together—your brain performs better with context consistency."
  },
  {
    text: "Set a daily cap on new incoming work so your list doesn't balloon and kill motivation."
  },
  {
    text: "Practice saying 'let me find the best answer for you' instead of guessing and backtracking."
  },
  {
    text: "Track which solutions stick versus which create follow-up tickets—double down on what works."
  },
  {
    text: "Eliminate redundant approval steps that slow down customer-facing decisions."
  },
  {
    text: "Start support blocks by quickly scanning all tickets to sequence them by true priority."
  },
  {
    text: "Use customer data to anticipate needs, solving problems before customers report them."
  },
  {
    text: "Archive resolved tickets immediately so your active list stays manageable and clear."
  },
  {
    text: "Build 15-minute buffers between back-to-back support blocks to prevent decision fatigue."
  },
  {
    text: "Create decision trees for common issues so tier-one support handles more independently."
  },
  {
    text: "Measure your most draining task and dedicate resources to systematically reduce it."
  },
  {
    text: "Validate that you understand the real problem before offering solutions—saves rework time."
  },
  {
    text: "Keep your tools organized so you spend minutes finding resources, not hours."
  },
  {
    text: "Notice which communication styles land best with different customer types and adjust early."
  },
  {
    text: "Protect your first hour of the day for high-value support work before notifications pile up."
  },
  {
    text: "Use video responses for complex issues—faster to record than type and shows genuine care."
  },
  {
    text: "Batch similar tasks together to minimize context switching and maintain deeper focus."
  },
  {
    text: "Set a specific end time for your workday to create urgency and prevent endless task creep."
  },
  {
    text: "Review your calendar before bed so your brain can process priorities while you sleep."
  },
  {
    text: "Use the two-minute rule: if a task takes less than two minutes, do it immediately."
  },
  {
    text: "Block your most creative hours for work that requires deep thinking, not meetings."
  },
  {
    text: "Saying no to one thing means saying yes to something that matters more."
  },
  {
    text: "Start with your hardest task first when your willpower and energy are highest."
  },
  {
    text: "Break large projects into milestones so progress feels tangible and motivating."
  },
  {
    text: "Reduce decision fatigue by automating or templating repetitive choices."
  },
  {
    text: "Track what actually distracts you for a week to identify your real productivity blockers."
  },
  {
    text: "Schedule focused work blocks with the same commitment you give to client meetings."
  },
  {
    text: "Your inbox should be a processing tool, not a to-do list or filing system."
  },
  {
    text: "Identify your peak productivity hours and protect them from low-value interruptions."
  },
  {
    text: "Use friction intentionally: make bad habits harder and good habits easier to start."
  },
  {
    text: "Review completed work weekly to build confidence and spot efficiency patterns."
  },
  {
    text: "Delegate or delete tasks that don't move you toward your actual goals."
  },
  {
    text: "Create a shutdown ritual to mentally close your workday and prevent evening rumination."
  },
  {
    text: "Limit work-in-progress to three things maximum to avoid the illusion of productivity."
  },
  {
    text: "Build in buffer time between tasks so you're not always rushing and context-switching."
  },
  {
    text: "Write down distracting thoughts immediately so your brain stops trying to remember them."
  },
  {
    text: "Prioritize based on impact, not urgency, at least one day per week."
  },
  {
    text: "Use time blocking for specific task types, not just individual tasks."
  },
  {
    text: "Your energy matters more than your hours—optimize for when you do your best work."
  },
  {
    text: "Batch email responses into two or three focused sessions instead of constant checking."
  },
  {
    text: "Remove notifications from everything except your most critical communication channels."
  },
  {
    text: "Plan tomorrow for 10 minutes today so you start with clarity, not chaos."
  },
  {
    text: "Create a 'done list' daily to train your brain to recognize progress."
  },
  {
    text: "Identify your three most important outcomes before adding anything else to your plate."
  },
  {
    text: "Use waiting time productively: respond to messages, plan, or think strategically."
  },
  {
    text: "Set boundaries on meeting length—many issues resolve faster with time limits."
  },
  {
    text: "Document your processes once so you don't mentally repeat the same decisions."
  },
  {
    text: "Protect your attention like you protect your calendar: both are finite resources."
  },
  {
    text: "Bundle similar small tasks to create momentum and finish them in one sprint."
  },
  {
    text: "Review what you didn't do as often as what you did to learn your real capacity."
  },
  {
    text: "Make your next action crystal clear before you finish your current task."
  },
  {
    text: "Use constraints to focus: a tight deadline often produces better work than unlimited time."
  },
  {
    text: "Schedule breaks as seriously as meetings—your brain needs recovery to sustain focus."
  },
  { text: "Identify the one metric that matters most and measure it weekly." },
  { text: "Say yes or no quickly rather than leaving decisions in limbo." },
  {
    text: "Your first task sets the tone for your day—choose something meaningful, not easy."
  },
  {
    text: "Reduce ambient stress by closing tabs, closing apps, and clearing your desk."
  },
  {
    text: "Batch administrative work into one block rather than spreading it throughout your day."
  },
  {
    text: "Track your energy, not just your time, to understand your real productivity patterns."
  },
  {
    text: "Use a single system for all your commitments so your brain can truly offload them."
  },
  {
    text: "Distinguish between being busy and making progress—they're rarely the same thing."
  },
  {
    text: "Review your last week's priorities before planning the next week's priorities."
  },
  {
    text: "Create a list of things you stop doing—subtraction is as important as addition."
  },
  {
    text: "Use the first hour of your day for work requiring clarity, not catch-up mode."
  },
  {
    text: "Communicate your availability clearly so interruptions decrease naturally."
  },
  {
    text: "Measure progress in completed projects, not completed tasks or hours worked."
  },
  {
    text: "Batch similar support tasks together to minimize context switching and mental fatigue."
  },
  {
    text: "Set a specific time each day to review and respond to messages rather than constant checking."
  },
  {
    text: "Break large customer issues into smaller subtasks to make progress feel more manageable."
  },
  {
    text: "Use templates for common responses, but always personalize the opening to feel genuine."
  },
  {
    text: "Schedule focused blocks for deep work on complex customer problems without interruptions."
  },
  {
    text: "Prioritize responding to customers with the longest wait times first each morning."
  },
  {
    text: "Identify your peak energy hours and reserve them for the most challenging interactions."
  },
  {
    text: "Create a checklist of steps for your most common support scenarios to save decision time."
  },
  {
    text: "Limit notifications to specific intervals rather than allowing real-time distractions."
  },
  {
    text: "Review yesterday's unresolved cases first thing to maintain continuity with customers."
  },
  {
    text: "Use keyboard shortcuts in Velaro to reduce time spent on repetitive navigation tasks."
  },
  {
    text: "Set realistic response time goals based on your team's actual capacity, not wishful thinking."
  },
  {
    text: "Block time after complex tickets to document solutions for your knowledge base."
  },
  {
    text: "Identify patterns in customer issues and address root causes rather than just symptoms."
  },
  {
    text: "Delegate routine questions to team members who enjoy them to boost overall efficiency."
  },
  {
    text: "Take a five-minute break between high-stress customer interactions to reset mentally."
  },
  {
    text: "Tag tickets by complexity level so you can batch similar difficulty work together."
  },
  {
    text: "Review your busiest hours and plan staffing to avoid bottlenecks during peak times."
  },
  {
    text: "Create a standard format for case notes that you can scan quickly when resuming tickets."
  },
  {
    text: "Set boundaries on which channels you check during off-hours to prevent burnout."
  },
  {
    text: "End each shift by marking what will be first priority tomorrow to ease the restart."
  },
  {
    text: "Use canned responses to handle the first 50 percent of most tickets, then customize."
  },
  {
    text: "Identify which communication style works best for each regular customer to save time."
  },
  {
    text: "Block distracting websites during your support hours to maintain focus on tickets."
  },
  {
    text: "Schedule team syncs after high-volume periods to share solutions discovered that day."
  },
  {
    text: "Ask customers clarifying questions upfront to avoid multiple back-and-forth exchanges."
  },
  {
    text: "Review metrics weekly to identify which processes are actually slowing you down."
  },
  {
    text: "Batch email responses once per hour rather than replying immediately to each one."
  },
  {
    text: "Create quick reference guides for your top five most complex product features."
  },
  {
    text: "Assign one team member per shift to handle escalations so others maintain focus."
  },
  {
    text: "Use Velaro's reporting to spot which ticket types take longest and improve those flows."
  },
  {
    text: "Set a timer for administrative tasks so they don't expand and consume your support time."
  },
  {
    text: "Read the customer's entire message before responding to avoid missing important details."
  },
  {
    text: "Create a lookup document for your most frequently asked questions to answer them faster."
  },
  {
    text: "Schedule complex customer calls during your most alert hours of the day for better focus."
  },
  {
    text: "Use conditional workflows in Velaro to automate routine triage and save manual steps."
  },
  {
    text: "Identify which tickets require your expertise versus those your team can fully handle."
  },
  {
    text: "Set expectations early that some complex issues require time to research properly."
  },
  {
    text: "Group similar feature requests together and share them with product as one consolidated list."
  },
  {
    text: "Clear your desk of non-work items during support hours to eliminate physical distractions."
  },
  {
    text: "Review your busiest ticket types and brainstorm solutions before they pile up daily."
  },
  {
    text: "Prioritize tickets from customers with highest lifetime value or contract value."
  },
  {
    text: "Use saved searches in Velaro to quickly pull up related previous cases and solutions."
  },
  {
    text: "Schedule your calendar so support work doesn't compete with other scheduled responsibilities."
  },
  {
    text: "Ask customers to provide screenshots or error codes upfront to speed up diagnosis."
  },
  {
    text: "Create a quick escalation guide so team members don't stall waiting for decisions."
  },
  {
    text: "Use status indicators in Velaro so teammates know when you're deep in complex issues."
  },
  {
    text: "Review one difficult ticket daily to learn and improve your troubleshooting approach."
  }
];

// ─── Interesting life & world facts ──────────────────────────────────────────
const LIFE_FACTS: WarmupMessage[] = [
  {
    text: "Honey never spoils — archaeologists found 3,000-year-old honey still edible"
  },
  { text: "Octopuses have three hearts and blue blood" },
  {
    text: "Your body produces a completely new skeleton roughly every 10 years"
  },
  { text: "Bananas are curved because they grow toward the sun" },
  { text: "A single cloud can weigh more than a million pounds" },
  { text: "Light from the sun takes about 8 minutes to reach Earth" },
  {
    text: "Sharks are older than trees — they've existed for 450 million years"
  },
  { text: "The average person walks 100,000 miles in their lifetime" },
  { text: "Ants can carry 50 times their own body weight" },
  {
    text: "Your brain uses 20% of the body's energy despite being only 2% of its weight"
  },
  { text: "A strawberry isn't a berry, but a banana technically is" },
  {
    text: "Laughter is contagious because humans are wired to mirror others' emotions"
  },
  { text: "Oxford University is older than the Aztec Empire" },
  {
    text: "The Eiffel Tower grows 6 inches taller in summer due to thermal expansion"
  },
  { text: "Crows can recognize and remember individual human faces for years" },
  { text: "Your heart generates enough pressure to squirt blood 30 feet" },
  { text: "Dolphins sleep with one eye open — half the brain stays awake" },
  { text: "Humans share 60% of their DNA with bananas" },
  {
    text: "Rain has a smell — petrichor — caused by a compound called geosmin"
  },
  { text: "Sea otters hold hands while sleeping so they don't drift apart" },
  { text: "The mantis shrimp can punch with the force of a bullet" },
  {
    text: "Hot water can freeze faster than cold water in some conditions — the Mpemba effect"
  },
  { text: "Trees communicate through underground fungal networks" },
  { text: "The Moon is moving away from Earth by about 1.5 inches per year" },
  { text: "Butterflies taste with their feet" },
  {
    text: "Saturn is less dense than water — it would float in a large enough ocean"
  },
  {
    text: "Flamingos are white — their pink color comes entirely from their food"
  },
  { text: "Humans are the only animals that blush" },
  {
    text: "Wombats produce cube-shaped droppings — the only animal known to do so"
  },
  { text: "Lightning strikes Earth about 100 times every second" },
  { text: "Your body has about 60,000 miles of blood vessels" },
  {
    text: "A spider's silk strand is stronger than steel of the same diameter"
  },
  {
    text: "Mount Olympus on Mars is nearly three times taller than Mount Everest"
  },
  {
    text: "Babies are born with 270 bones — adults end up with 206 as many fuse"
  },
  { text: "Polar bears' fur is transparent, not white — it scatters light" },
  {
    text: "Cows have best friends and produce more milk when near their favorite companion"
  },
  {
    text: "DNA in your body, stretched end to end, would reach the sun and back 600 times"
  },
  {
    text: "Your heart beats about 100,000 times a day and 35 million times a year"
  },
  {
    text: "A caterpillar essentially dissolves into soup inside its cocoon before becoming a butterfly"
  },
  {
    text: "Identical twins don't have the same fingerprints — they're shaped by fetal movement"
  },
  {
    text: "Crows can hold grudges and teach other crows to dislike specific humans"
  },
  {
    text: "Humans are the only species that cooks food — which dramatically shaped our evolution"
  },
  {
    text: "Earth is the only planet in our solar system not named after a god"
  },
  {
    text: "The word 'salary' comes from the Latin for salt — Romans were sometimes paid in salt"
  },
  {
    text: "The ocean produces over 50% of Earth's oxygen — mostly from phytoplankton"
  },
  {
    text: "Running and walking a mile burn nearly the same number of calories"
  },
  {
    text: "The first computer bug was a real bug — a moth found in a Harvard relay in 1947"
  },
  {
    text: "Raindrops are shaped like hamburger buns, not teardrops, due to air resistance"
  },
  {
    text: "The color blue was one of the last colors to be named in most ancient languages"
  },
  {
    text: "Squirrels forget about half their buried nuts — accidentally planting millions of trees"
  },
  {
    text: "Your fingernails grow about 3.5mm per month — toenails about half as fast"
  },
  { text: "The skin is the human body's largest organ" },
  {
    text: "There are more species of beetles on Earth than any other animal — over 400,000"
  },
  { text: "A single mature tree can absorb 48 pounds of CO₂ per year" },
  { text: "There are more trees on Earth than stars in the Milky Way" },
  { text: "Babies laugh about 300 times per day — adults average around 20" },
  {
    text: "Orcas (killer whales) are actually the largest member of the dolphin family"
  },
  {
    text: "Your brain is roughly 73% water — dehydration directly impairs thinking"
  },
  {
    text: "Music activates more areas of the brain simultaneously than any other activity"
  },
  { text: "The sense of touch is the first sense to develop in the womb" },
  {
    text: "Glass catfish are nearly transparent — you can see their organs through their bodies"
  },
  {
    text: "Astronauts grow up to 2 inches taller in space due to spinal decompression"
  },
  { text: "Penguins propose to their mates with a carefully chosen pebble" },
  {
    text: "The Great Barrier Reef is the largest living structure on Earth, visible from space"
  },
  { text: "There are 118 elements in the periodic table — 98 occur naturally" },
  { text: "The universe is approximately 13.8 billion years old" },
  { text: "You share DNA with every living organism on Earth" },
  {
    text: "Cleopatra lived closer in time to the Moon landing than to the Great Pyramid's construction"
  },
  { text: "A group of flamingos is called a flamboyance" },
  { text: "The platypus is one of the few venomous mammals" },
  {
    text: "Newborns don't have kneecaps — they develop over the first few years of life"
  },
  {
    text: "The longest recorded hiccup lasted 68 years — Charles Osborne, 1922 to 1990"
  },
  {
    text: "There are 10 times more bacterial cells in your body than human cells"
  },
  {
    text: "Your left lung is smaller than your right — it makes room for your heart"
  },
  {
    text: "An apple, potato, and onion taste nearly identical when eaten with your nose plugged"
  },
  {
    text: "Roosters have a built-in mechanism that partially deafens their own crow"
  },
  {
    text: "Starfish have no brain and no blood — they use seawater as their circulatory fluid"
  },
  {
    text: "Baby blue whales gain about 200 pounds per day in their first year"
  },
  { text: "Your bones are roughly 4 times stronger than concrete by weight" },
  {
    text: "There is a jellyfish (Turritopsis dohrnii) that is biologically immortal"
  },
  {
    text: "Elephants are the only animals known to hold ceremonies around their dead"
  },
  {
    text: "Goldfish have a memory span well over 3 months — the '3-second' myth is false"
  },
  { text: "Hummingbirds are the only birds that can fly backward" },
  {
    text: "There are more microorganisms in a teaspoon of soil than humans on Earth"
  },
  {
    text: "The human body is bioluminescent — we glow faintly, just beyond visible range"
  },
  { text: "Wolves howl in different dialects depending on where they're from" },
  { text: "Plants respond to the sound of running water by growing toward it" },
  {
    text: "Coral reefs cover less than 1% of the ocean floor but support 25% of all marine life"
  },
  {
    text: "Lobsters were once considered a food of the poor — fed to prisoners in colonial America"
  },
  { text: "A group of owls is called a parliament" },
  {
    text: "Stars don't actually twinkle — it's Earth's atmosphere distorting the light"
  },
  { text: "Whales sing to each other across hundreds of miles of ocean" },
  {
    text: "The original Ferris wheel was built for the 1893 World's Fair in Chicago"
  },
  {
    text: "The Mona Lisa has no eyebrows — they were fashionable to shave in Renaissance Florence"
  },
  {
    text: "Beethoven composed some of his greatest works while completely deaf"
  },
  {
    text: "Honey bees must visit about 2 million flowers to make one pound of honey"
  },
  {
    text: "There are more public libraries in the US than McDonald's restaurants"
  },
  {
    text: "The Great Wall of China is not visible from space with the naked eye"
  },
  {
    text: "A group of ravens is called a conspiracy — or sometimes an unkindness"
  },
  { text: "10% of all photos ever taken were taken in the last 12 months" },
  {
    text: "There are more stars in the universe than grains of sand on all of Earth's beaches"
  },
  { text: "Ice cream was invented in China before it reached Europe" },
  { text: "A group of butterflies is called a kaleidoscope" },
  { text: "Each human ear contains over 30,000 hair cells that detect sound" },
  { text: "A bolt of lightning is 5 times hotter than the surface of the sun" },
  { text: "The average human dream lasts only 20–30 minutes" },
  { text: "The human nose can detect more than 1 trillion different scents" },
  {
    text: "Your brain's storage capacity is effectively unlimited — roughly 2.5 petabytes"
  },
  {
    text: "Octopuses can edit their own RNA to adapt to changing temperatures"
  },
  { text: "A day on Venus is longer than a year on Venus" },
  { text: "Every second, about 100 lightning bolts strike the Earth" },
  {
    text: "Sunflowers track the sun across the sky — a process called heliotropism"
  },
  {
    text: "Spiders eat more insects by weight each year than the entire human meat intake"
  },
  {
    text: "The word 'muscle' comes from the Latin for 'little mouse' — a flexed bicep looks like one"
  },
  { text: "Saturn's moon Titan has rain — but it rains methane, not water" },
  {
    text: "There are more possible chess games than atoms in the observable universe"
  },
  {
    text: "The shortest scheduled flight is 57 seconds, from Westray to Papa Westray, Scotland"
  },
  { text: "Every 60 seconds, 1,800 thunderstorms occur somewhere on Earth" },
  {
    text: "Your eyes can distinguish approximately 10 million different colors"
  },
  { text: "A group of hedgehogs is called a prickle" },
  { text: "The human body sheds about 9 pounds of skin cells every year" },
  {
    text: "You blink about 15–20 times per minute, yet rarely notice yourself doing it"
  },
  { text: "Elephants are the only animals that can't jump" },
  { text: "Crocodiles haven't changed significantly in 200 million years" },
  { text: "The average lifespan of a taste bud is only 10 days" },
  {
    text: "Trees in urban areas live an average of 13 years — rural trees live hundreds"
  },
  {
    text: "A single raindrop falling on dry soil releases up to 2,000 tiny droplets on impact"
  },
  {
    text: "The color of the sky at sunset tells you about the dust and moisture in the air"
  },
  {
    text: "A group of cats is called a clowder; a group of kittens is called a kindle"
  },
  {
    text: "Ants don't sleep — they take hundreds of 1-minute power naps throughout the day"
  },
  {
    text: "The human eye moves about 200 degrees per second — one of the body's fastest motions"
  },
  {
    text: "Dogs can detect seizures before they happen by sensing biochemical changes"
  },
  {
    text: "A cloud of gas in the constellation Sagittarius smells like raspberries"
  },
  { text: "The world's oldest living tree is over 5,000 years old" },
  { text: "Your skin completely renews itself every 2–4 weeks" },
  {
    text: "The average person forgets 50% of new information within an hour of learning it"
  },
  {
    text: "Humans are one of the few species capable of recognizing themselves in a mirror"
  },
  {
    text: "There are more possible iterations of a shuffled deck of cards than atoms in Earth"
  },
  { text: "A group of meerkats is called a mob or a gang" },
  {
    text: "The human circulatory system, laid end to end, would wrap around Earth twice"
  },
  { text: "The sense of smell is the sense most strongly linked to memory" },
  { text: "Typewriter uses only the top row of keys on a QWERTY keyboard" },
  {
    text: "Trees can send chemical distress signals to each other when under insect attack"
  },
  {
    text: "There are more than 1 million described insect species — millions more undiscovered"
  },
  {
    text: "The original meaning of 'nice' was foolish or silly — language evolves dramatically"
  },
  {
    text: "Coral polyps build massive reefs from their own calcium carbonate skeletons"
  },
  { text: "Iguanas can hold their breath for up to 45 minutes underwater" },
  {
    text: "The fingerprints of koalas are nearly indistinguishable from human fingerprints"
  },
  { text: "A group of porcupines is also called a prickle" },
  {
    text: "The first words ever spoken on the telephone: 'Mr. Watson, come here — I want to see you'"
  },
  {
    text: "Honey's antibacterial properties were so reliable it was used on wounds in WWI"
  },
  {
    text: "Rainforest trees 'talk' by releasing chemical signals into the air"
  },
  { text: "Your brain is more active at night than during the day" },
  {
    text: "The longest English word typeable with only the left hand is 'stewardesses'"
  },
  {
    text: "More people die annually from falling coconuts than from shark attacks"
  },
  { text: "A group of gorillas is called a band or a troop" },
  {
    text: "Every human on Earth descended from a common maternal ancestor about 200,000 years ago"
  },
  {
    text: "Crows make and use tools — they'll shape sticks to reach food in a hole"
  },
  {
    text: "The universe is expanding, and the most distant galaxies are receding faster than light"
  },
  { text: "A snail can sleep for up to 3 years during extreme drought" },
  { text: "There are roughly 8 million lightning strikes on Earth every day" },
  { text: "The average human sneeze travels at about 100 miles per hour" },
  { text: "Muscles are the last organ to stop functioning after death" },
  {
    text: "The human body produces about 25,000 quarts of saliva in a lifetime"
  },
  { text: "A group of jellyfish is called a bloom or a smack" },
  {
    text: "The first video game was created in 1958 — a tennis simulation on an oscilloscope"
  },
  {
    text: "Every atom in your body was forged inside a star billions of years ago"
  },
  {
    text: "Sea otters use rocks as tools — and often have a 'favorite' rock they keep"
  },
  { text: "The Amazon rainforest produces 20% of the world's oxygen" },
  {
    text: "Bees can recognize human faces — they use the same feature-assembly method we do"
  },
  { text: "There are more than 40,000 distinct species of spiders on Earth" },
  { text: "The human brain generates about 70,000 thoughts per day" },
  {
    text: "A day on Mars is 24 hours 37 minutes — almost identical to Earth's"
  },
  { text: "An eagle's eyesight is 4–8 times stronger than a human's" },
  {
    text: "Your heart can continue beating outside your body as long as it gets oxygen"
  },
  {
    text: "The number of possible positions in a chess game exceeds 10 to the power of 120"
  },
  {
    text: "Giraffes have the same number of neck vertebrae as humans — just much larger ones"
  },
  { text: "Blind people dream — just with other senses rather than images" },
  {
    text: "The Sahara Desert was green and lush as recently as 5,000 years ago"
  },
  {
    text: "Pigeons can recognize themselves in mirrors and distinguish individual humans"
  },
  {
    text: "A blue whale's heartbeat is so loud it can be detected from 2 miles away"
  },
  {
    text: "The average cloud travels at 30–40 mph and can weigh 500 million pounds"
  },
  { text: "You have a unique tongue print — just like your fingerprints" },
  {
    text: "The world's largest known organism is a honey fungus in Oregon, covering 2,385 acres"
  },
  {
    text: "Parrots don't just mimic — some demonstrate actual understanding of words and concepts"
  },
  {
    text: "Lake Baikal contains 20% of all the world's unfrozen fresh surface water"
  },
  { text: "Ancient Romans used crushed mouse brains as toothpaste" },
  { text: "The letters in 'listen' are an anagram of 'silent'" },
  { text: "Your lungs have a surface area roughly the size of a tennis court" },
  {
    text: "Cats have a third eyelid, called a nictitating membrane, rarely seen in healthy cats"
  },
  { text: "A group of hippos is called a bloat" },
  {
    text: "Sharks have been relatively unchanged for 450 million years — before the first trees"
  },
  { text: "The Pacific Ocean is wider than the Moon is from Earth" },
  { text: "There is enough iron in your body to make a small nail" },
  {
    text: "The world's quietest room absorbs 99.99% of sound — people hallucinate in it within minutes"
  },
  { text: "Every continent on Earth ends in the same letter it starts with" },
  {
    text: "Sloths can hold their breath longer than dolphins — up to 40 minutes"
  },
  {
    text: "Your stomach gets a new lining every 3–4 days to prevent it from digesting itself"
  },
  { text: "A group of crows is called a murder" },
  {
    text: "Elephants are the only animals known to show empathy for dying members of other species"
  },
  {
    text: "The shortest war in history lasted 38 minutes — the Anglo-Zanzibar War of 1896"
  },
  {
    text: "A shark must keep swimming or water won't pass through its gills — most will drown if still"
  },
  {
    text: "Mantis shrimps can see 16 types of color receptors — humans have only 3"
  },
  {
    text: "The dead star 'Lucy' in Canis Major is a diamond about the size of Earth"
  },
  {
    text: "Most of the universe (about 95%) is made of dark matter and dark energy we can't see"
  },
  { text: "You are physically unable to hum while holding your nose" },
  { text: "A group of ferrets is called a business" },
  {
    text: "Tardigrades (water bears) can survive the vacuum of space, radiation, and boiling water"
  },
  {
    text: "The average American walks about 3,000–4,000 steps per day — far below the recommended 10,000"
  },
  {
    text: "You can hear your heartbeat in your ears when you press them against a pillow"
  },
  { text: "Honeybees can recognize faces and remember them for days" },
  {
    text: "The world's oldest joke was written in Sumeria around 1900 BC — it's about a shoe"
  },
  {
    text: "Paper was invented in China around 105 AD — books existed for 3,000 years before that"
  },
  {
    text: "The word 'quarantine' comes from 40 days — 'quarantina' in Italian"
  },
  { text: "An ostrich's eye is bigger than its brain" },
  {
    text: "Neutron stars are so dense that a teaspoon of one weighs about 6 billion tons"
  },
  {
    text: "A woodpecker's tongue wraps around its skull to cushion its brain during pecking"
  },
  {
    text: "The driest place on Earth is Antarctica's Dry Valleys — no rain for 2 million years"
  },
  {
    text: "Pistol shrimp snap their claws so fast they create a bubble hotter than the sun's surface"
  },
  {
    text: "The total length of all your body's neurons, end to end, would reach the Moon and back"
  },
  {
    text: "Glass is technically not a solid or a liquid — it's an amorphous solid"
  },
  {
    text: "The Voyager 1 spacecraft is over 15 billion miles from Earth and still transmitting data"
  },
  {
    text: "An average cumulonimbus thunderstorm cloud contains the energy of 10 atomic bombs"
  },
  {
    text: "Axolotls can regenerate entire limbs, spinal cords, hearts, and parts of their brains"
  },
  {
    text: "Astronauts' fingernails often fall off in space due to pressure in spacesuit gloves"
  },
  {
    text: "The human brain can process an image in as little as 13 milliseconds"
  },
  {
    text: "The oldest known living organism is a seagrass meadow in Australia, over 100,000 years old"
  },
  { text: "Sound travels about 4.3 times faster in water than in air" },
  {
    text: "A cubic inch of human bone can bear a load of 19,000 pounds — roughly 4 pickup trucks"
  },
  {
    text: "The electric eel can produce shocks of up to 860 volts — enough to stun a horse"
  },
  {
    text: "Bioluminescent organisms in the ocean produce more light than all human-made sources combined"
  },
  {
    text: "The longest mountain range on Earth is underwater — the Mid-Atlantic Ridge stretches 40,000 miles"
  },
  {
    text: "Chameleons don't change color for camouflage — they do it to communicate mood and temperature"
  },
  {
    text: "Your DNA contains enough information to stretch from the Earth to Pluto and back — 17 times"
  },
  {
    text: "Honey badgers can withstand venomous snake bites that would kill most animals"
  },
  { text: "The human body contains enough carbon to make about 9,000 pencils" },
  { text: "Glaciers store about 69% of the world's fresh water" },
  {
    text: "A photon of light takes 8 minutes to reach Earth but 100,000 years to leave the sun's core"
  },
  { text: "The tongue of a blue whale weighs as much as an elephant" },
  {
    text: "The mimic octopus can impersonate over 15 different marine species to avoid predators"
  },
  { text: "Octopuses have copper-based blood — it turns blue when oxygenated" },
  {
    text: "The coldest temperature ever recorded on Earth was minus 128.6 degrees Fahrenheit in Antarctica"
  },
  {
    text: "A hummingbird's heart beats more than 1,200 times per minute during flight"
  }
];

// ─── Motivational ─────────────────────────────────────────────────────────────
const MOTIVATIONAL: WarmupMessage[] = [
  { text: "Every great conversation starts with a great team" },
  { text: "Building connections, one chat at a time" },
  { text: "Great support starts right here" },
  { text: "Another great day of work starts now" },
  { text: "Powering better customer experiences" },
  { text: "Every message is an opportunity to make someone's day better" },
  { text: "Making support smarter, one interaction at a time" },
  { text: "Where great teams deliver great experiences" },
  { text: "Built for teams who genuinely care about people" },
  { text: "The right tools make great teams even better" },
  { text: "Your configuration shapes the experience every customer has" },
  { text: "Small improvements every day become transformational over time" },
  { text: "The work you do here ripples out to thousands of customers" },
  {
    text: "Great teams are not born — they are built through small daily commitments"
  },
  { text: "Today is a good day to help someone solve their problem" },
  {
    text: "Customer experience is the competitive advantage that cannot be copied"
  },
  { text: "The best support teams never stop asking how they can get better" },
  {
    text: "Excellence in support is a habit — and habits are built by showing up"
  },
  {
    text: "The best time to improve a process was yesterday — the second best is right now"
  },
  {
    text: "Customers remember how you fixed the problem more than the problem itself"
  },
  {
    text: "A team that communicates well can outperform a team twice its size"
  },
  {
    text: "Behind every great customer experience is a team that refuses to settle"
  },
  { text: "Consistency builds trust — show up the same way every time" },
  { text: "The difference between good and great is one more question asked" },
  {
    text: "Your tools are only as good as the people who use them intentionally"
  },
  {
    text: "Start each interaction like it's the customer's first — because it might be"
  },
  { text: "What you measure improves — what you celebrate improves faster" },
  {
    text: "Every resolved conversation is proof that your team made someone's day better"
  }
];

// Fisher-Yates shuffle — runs once at module load so every session gets
// a different order, but the loading messages always come first.
function shuffle<T>(arr: T[]): T[] {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

// Loading messages stay at the front so they appear while the app is
// actually loading. Everything else is shuffled into a random order.
//
// Weighting: positivity, platform tips, call-center facts, and productivity
// each appear 3× as often as life facts (1×) and motivational (1×).
// This creates a warm, useful experience weighted toward growth and service quality.
export const WARMUP_MESSAGES: WarmupMessage[] = [
  ...LOADING,
  ...shuffle([
    ...PLATFORM_TIPS, // 3× weight
    ...PLATFORM_TIPS,
    ...PLATFORM_TIPS,
    ...FACTS, // 3× weight — call-center & customer service facts
    ...FACTS,
    ...FACTS,
    ...POSITIVITY, // 3× weight — mental wellness & resilience
    ...POSITIVITY,
    ...POSITIVITY,
    ...PRODUCTIVITY, // 3× weight — productivity & work habits
    ...PRODUCTIVITY,
    ...PRODUCTIVITY,
    ...LIFE_FACTS, // 1× — interesting life/science/world facts
    ...MOTIVATIONAL // 1× — brief inspirational
  ])
];

export const LOADING_COUNT = LOADING.length;
